import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  adminAPI,
  categoryAPI,
  eventBatchAPI,
  trekAPI,
  eventTypeAPI,
  locationAPI,
  refundCalculatorCategoryAPI
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useListQueryParams from '../hooks/useListQueryParams';
import { packageDayPayloadFromForm } from '../utils/packageDayFields.js';
import { formatAdminPrice, priceInputString } from '../utils/formatAdminPrice.js';
import {
  normalizeQuillHtml,
  normalizeQuillEmpty,
  htmlHasTable,
  htmlHasRichContent,
  prepareRichHtmlForEditor,
  resolvePolicyRichHtmlForSave
} from '../utils/richHtml.js';
import { sanitizeRichHtml } from '../utils/sanitizeRichHtml.js';
import { canManageTreks as canManageTreksPermission } from '../utils/trekPermissions.js';
import FullIconSelect from '../components/FullIconSelect.jsx';
import AdminRichTextEditor from '../components/AdminRichTextEditor.jsx';
import {
  TREK_DIFFICULTY_GROUPS,
  ALL_TREK_DIFFICULTY_VALUES
} from '../constants/trekDifficulty.js';
import { getIncludeSidebarLucideComponent } from '../utils/feeIncludeIcons.js';
import { ArrowLeft, GripVertical, Trash2 } from 'lucide-react';
import { PageHeader, FilterBar, EmptyState, StatusChip } from '../components/ui';
import './ModuleAdmin.css';
import WebstoriesManager from '../components/WebstoriesManager.jsx';

/** Gallery image URL from API (camelCase or snake_case). */
function expGalleryItemImageUrl(item) {
  return String(item?.url ?? item?.image_url ?? item?.imageUrl ?? '').trim();
}

function expGalleryItemVideoId(item) {
  return String(item?.videoId ?? item?.video_id ?? '').trim();
}

/** YouTube video ids are typically 11 chars; allow common variants. */
function looksLikeYouTubeVideoId(id) {
  const v = String(id || '').trim();
  return v.length >= 6 && v.length <= 32 && /^[a-zA-Z0-9_-]+$/.test(v);
}

/**
 * Poster / thumbnail controls only for real YouTube rows.
 * Past data: `type === 'image'` but stray `video_id` — still a photo, no Poster.
 */
function expGalleryItemIsVideoTile(item) {
  const t = String(item?.type ?? '').toLowerCase().trim();
  const imageUrl = expGalleryItemImageUrl(item);
  const videoId = expGalleryItemVideoId(item);
  const yt = looksLikeYouTubeVideoId(videoId);
  if (t === 'image') return false;
  if (t === 'video') return yt && !imageUrl;
  if (imageUrl) return false;
  return yt;
}

/** Fields hydrated from API that must not be sent back on update. */
const TREK_FORM_READONLY_KEYS = new Set([
  'id',
  'createdAt',
  'updatedAt',
  'totalBatches',
  'category',
  'locationMaster',
  'eventType',
  'consultant',
  'packages',
  'scheduleDays',
  'attractions',
  'feesIncludes',
  'instructions',
  'media',
  'videos',
  'experienceGallery',
  'batches',
  'similarTreks',
  'campsite'
]);

function normalizeTrekStatusFromApi(item) {
  const raw = String(item?.status || '').toLowerCase();
  if (raw === 'active' || raw === 'inactive' || raw === 'draft') return raw;
  return item?.isActive === false ? 'inactive' : 'active';
}

const emptyForm = {
  campsiteId: '',
  name: '',
  code: '',
  slug: '',
  tagline: '',
  metaTitle: '',
  metaDescription: '',
  description: '',
  difficulty: '',
  location: '',
  videoId: '',
  lengthKm: '',
  minAge: '',
  maxAge: '',
  altitude: '',
  brochureUrl: '',
  tipsUrl: '',
  importantUpdate: '',
  faqs: '[]',
  quickPolicyLinks: [],
  status: 'active',
  advancePaymentEnabled: true,
  categoryId: '',
  locationId: '',
  refundCalculatorCategoryId: '',
  eventTypeIds: [],
  order: 0,
  similarTrekIds: [],
  leadTemplates: null,
  stayOptions: [],
  stayDetails: [],
  badges: [],
  logisticsIcons: { duration: '', lengthKm: '', age: '', difficulty: '', altitude: '' }
};

const slugifyTrekName = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

function trekPayloadFingerprint(payload) {
  const p = { ...(payload || {}) };
  delete p.status;
  delete p.isActive;
  return JSON.stringify(p);
}

function emptyLogisticsIcons() {
  return { duration: '', lengthKm: '', age: '', difficulty: '', altitude: '' };
}

function mergeLogisticsIconsFromApi(raw) {
  const b = emptyLogisticsIcons();
  const o = raw && typeof raw === 'object' ? raw : {};
  for (const k of Object.keys(b)) {
    if (o[k] != null && String(o[k]).trim()) b[k] = String(o[k]).trim();
  }
  return b;
}

function TrekPreviewMetaPills({ meta }) {
  const { LengthIcon, DifficultyIcon, kmText, difficultyText } = meta;
  return (
    <div className="trek-preview-meta">
      <span className="trek-preview-meta-pill">
        <LengthIcon className="trek-preview-meta-icon" size={14} strokeWidth={2} aria-hidden />
        {kmText}
      </span>
      <span className="trek-preview-meta-pill">
        <DifficultyIcon className="trek-preview-meta-icon" size={14} strokeWidth={2} aria-hidden />
        {difficultyText}
      </span>
    </div>
  );
}

function newQuickPolicyRowId() {
  return `qp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function parseJsonArrayField(raw) {
  if (raw == null) return [];
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return Array.isArray(raw) ? raw : [];
}

function parseQuickPolicyLinksRaw(raw) {
  return parseJsonArrayField(raw);
}

/** API may nest `{ trek: { … } }` (e.g. batch rows); admin form expects a flat trek. */
function unwrapTrekApiRow(raw) {
  if (raw == null || typeof raw !== 'object') return raw;
  const t = raw.trek;
  if (t && typeof t === 'object') return { ...t };
  return raw;
}

function quickPolicyEntryBody(x) {
  if (x == null) return '';
  const raw = x.content ?? x.body ?? x.html ?? x.text ?? x.details;
  if (raw == null) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object' && raw !== null && typeof raw.html === 'string') return raw.html;
  return String(raw);
}

/** When quick_policy_links rows have titles only, pull HTML from legacy trek columns. */
function legacyQuickPolicyContentForTitle(title, item) {
  const t = String(title || '').trim().toLowerCase();
  if (!t) return '';
  if (t.includes('cancellation')) {
    return String(item.cancellationPolicy ?? item.cancellation_policy ?? '').trim();
  }
  if (t.includes('terms') && t.includes('condition')) {
    return String(item.termsAndConditions ?? item.terms_and_conditions ?? '').trim();
  }
  if (t.includes('things') && (t.includes('carri') || t.includes('carry'))) {
    return String(item.thingsToCarry ?? item.things_to_carry ?? '').trim();
  }
  return '';
}

/** Module-level quick policy editor state (must exist before any trek editor callback runs). */
const quickPolicyContentBackupStore = new Map();
const quickPolicyEditorRefStore = {};

function deriveQuickPolicyLinksForForm(item) {
  const raw = parseQuickPolicyLinksRaw(item.quickPolicyLinks ?? item.quick_policy_links);
  const tid = item.id != null ? String(item.id) : 'new';
  if (raw.length > 0) {
    return raw.map((x, i) => {
      const title = String(x.title || '').trim();
      let content = prepareRichHtmlForEditor(quickPolicyEntryBody(x));
      if (!content) content = prepareRichHtmlForEditor(legacyQuickPolicyContentForTitle(title, item));
      return {
        id: String(x.id || '').trim() || `qp-${tid}-${i}`,
        icon: String(x.icon || 'briefcase').trim(),
        title,
        content
      };
    });
  }
  const legacy = [];
  if (String(item.thingsToCarry || item.things_to_carry || '').trim()) {
    legacy.push({
      id: `qp-${tid}-legacy-0`,
      icon: 'briefcase',
      title: 'Things To Be Carried',
      content: prepareRichHtmlForEditor(item.thingsToCarry || item.things_to_carry || '')
    });
  }
  if (String(item.cancellationPolicy || item.cancellation_policy || '').trim()) {
    legacy.push({
      id: `qp-${tid}-legacy-1`,
      icon: 'clock',
      title: 'Cancellation Policy',
      content: prepareRichHtmlForEditor(item.cancellationPolicy || item.cancellation_policy || '')
    });
  }
  if (String(item.termsAndConditions || item.terms_and_conditions || '').trim()) {
    legacy.push({
      id: `qp-${tid}-legacy-2`,
      icon: 'shield',
      title: 'Terms & Conditions',
      content: prepareRichHtmlForEditor(item.termsAndConditions || item.terms_and_conditions || '')
    });
  }
  return legacy;
}

const NestedTable = ({
  title,
  rows,
  onCreate,
  onUpdate,
  onDelete,
  defaults,
  fields,
  parentId = null,
  infoMessage = '',
  addLabel = 'Add',
  createSubmitLabel = 'Save',
  directUpload = false,
  uploadFieldKey = 'url',
  uploadMultiple = false,
  promptAdd = false,
  promptMessage = '',
  mapPromptValue = null,
  allowEdit = true
}) => {
  const [form, setForm] = useState({ ...defaults });
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isDirectUploading, setIsDirectUploading] = useState(false);
  const uploadInputRef = useRef(null);
  const { showToast } = useToast();

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await onUpdate(editId, form);
      else await onCreate(form);
      setForm({ ...defaults });
      setEditId(null);
      setShowForm(false);
    } catch (error) {
      showToast({
        message: error?.response?.data?.message || 'Failed to save section data.',
        severity: 'error'
      });
    }
  };

  const onDirectUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    try {
      setIsDirectUploading(true);
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const payload = {
          ...defaults,
          [uploadFieldKey]: file
        };

        if (Object.prototype.hasOwnProperty.call(payload, 'title') && !payload.title) {
          payload.title = file.name.replace(/\.[^/.]+$/, '');
        }
        if (Object.prototype.hasOwnProperty.call(payload, 'order')) {
          payload.order = (rows?.length || 0) + index;
        }

        // Sequential upload keeps order deterministic and avoids API bursts.
        // eslint-disable-next-line no-await-in-loop
        await onCreate(payload);
      }
    } catch (error) {
      showToast({
        message: error?.response?.data?.message || 'Failed to upload images.',
        severity: 'error'
      });
    } finally {
      setIsDirectUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="legacy-section">
      <div className="legacy-section-head">
        <h3>{title}</h3>
        <button
          className="module-btn legacy-add-btn"
          type="button"
          disabled={isDirectUploading}
          onClick={async () => {
            if (directUpload) {
              uploadInputRef.current?.click();
              return;
            }
            if (promptAdd) {
              const value = window.prompt(promptMessage || `Enter ${title}`);
              if (!value || !value.trim()) return;
              try {
                const payload = mapPromptValue ? mapPromptValue(value.trim()) : { value: value.trim() };
                await onCreate(payload);
              } catch (error) {
                showToast({
                  message: error?.response?.data?.message || `Failed to add ${title}.`,
                  severity: 'error'
                });
              }
              return;
            }
            setShowForm(true);
            setEditId(null);
            setForm({ ...defaults });
          }}
        >
          {isDirectUploading ? 'Uploading...' : addLabel}
        </button>
        {directUpload ? (
          <input
            ref={uploadInputRef}
            type="file"
            accept="image/*"
            multiple={uploadMultiple}
            style={{ display: 'none' }}
            onChange={onDirectUpload}
          />
        ) : null}
      </div>
      {infoMessage ? (
        <div className="legacy-disabled-note">{infoMessage}</div>
      ) : null}
      <table className="module-table">
        <thead>
          <tr>
            {fields.map((f) => <th key={f.key}>{f.label}</th>)}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((row) => (
            <tr key={row.id}>
              {fields.map((f) => <td key={f.key}>{row[f.key]}</td>)}
              <td className="module-actions">
                {allowEdit ? (
                  <button
                    className="module-link-btn"
                    onClick={() => {
                      setEditId(row.id);
                      setShowForm(true);
                      setForm(fields.reduce((acc, f) => ({ ...acc, [f.key]: row[f.key] ?? '' }), {}));
                    }}
                  >
                    Edit
                  </button>
                ) : null}
                <button
                  className="module-link-btn danger"
                  onClick={() => {
                    onDelete(row.id);
                  }}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {showForm && (!directUpload || !!editId) ? (
        <form className="legacy-inline-form" onSubmit={onSubmit}>
          <div className="module-form-grid">
            {fields.map((field) => (
              <div key={field.key}>
                <label>{field.label}</label>
                {field.type === 'image-file' ? (
                  <div>
                    {typeof form[field.key] === 'string' && form[field.key] ? (
                      <small style={{ display: 'block', marginBottom: '0.35rem', color: '#6b7280' }}>
                        Existing image will be kept unless you choose a new file.
                      </small>
                    ) : null}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setForm((p) => ({ ...p, [field.key]: e.target.files?.[0] || '' }))}
                      required={field.required && !editId}
                    />
                  </div>
                ) : (
                  <input
                    type={field.type || 'text'}
                    value={form[field.key] ?? ''}
                    onChange={(e) => setForm((p) => ({ ...p, [field.key]: e.target.value }))}
                    required={field.required}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="legacy-inline-actions">
            <button className="module-btn" type="submit">{editId ? 'Update' : createSubmitLabel}</button>
            <button
              className="module-btn secondary"
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditId(null);
                setForm({ ...defaults });
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
};

const Treks = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedTrekId, setSelectedTrekId] = useState(null);
  /** Full GET hydrates long HTML; list rows can be incomplete. */
  const [trekEditorLoading, setTrekEditorLoading] = useState(false);
  /** Remount / sync rich editors after server HTML is applied. */
  const [richTextMountKey, setRichTextMountKey] = useState(0);
  const blockRichEditorsWhileFetching = Boolean(editing) && trekEditorLoading;
  const [similarDropdownOpen, setSimilarDropdownOpen] = useState(false);
  const similarDropdownRef = useRef(null);
  const eventTypeMultiRef = useRef(null);
  const [eventTypeMultiOpen, setEventTypeMultiOpen] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [mediaDragId, setMediaDragId] = useState(null);
  const [badgeDragIdx, setBadgeDragIdx] = useState(null);
  const [isReorderingMedia, setIsReorderingMedia] = useState(false);
  const [isBrochureUploading, setIsBrochureUploading] = useState(false);
  const [isTipsUploading, setIsTipsUploading] = useState(false);
  const imageUploadInputRef = useRef(null);
  /** Snapshot after editor load — used so status stays put until the user actually edits. */
  const trekBaselineRef = useRef(null);
  const initialStatusRef = useRef('active');
  const [showPackageForm, setShowPackageForm] = useState(false);
  const [editingPackageId, setEditingPackageId] = useState(null);
  const [isPackageImageUploading, setIsPackageImageUploading] = useState(false);
  const packageImageInputRef = useRef(null);
  const expGalleryPhotoInputRef = useRef(null);
  const [expGalleryPhotoUrl, setExpGalleryPhotoUrl] = useState('');
  const [expGalleryVideoUrl, setExpGalleryVideoUrl] = useState('');
  const [isExpGalleryAdding, setIsExpGalleryAdding] = useState(false);
  const expGalleryVideoThumbInputRef = useRef(null);
  const [expGalleryVideoThumbItemId, setExpGalleryVideoThumbItemId] = useState(null);
  const [isExpGalleryThumbUploading, setIsExpGalleryThumbUploading] = useState(false);
  const { permissions } = useAuth();
  const canManageTreks = canManageTreksPermission(permissions);
  const canEditSlugs = permissions.includes('*') || permissions.includes('slug_manage');
  const [packageForm, setPackageForm] = useState({
    name: '',
    description: '',
    price: '',
    vacancy: '',
    startDayOffset: 0,
    endDayOffset: 0,
    priority: 0,
    imageUrl: '',
    reportingMessage: '',
    ticketRequired: false,
    ticketDeparture: false,
    ticketDepartureAddon: false,
    ticketReturn: false,
    ticketReturnAddon: false,
    hidden: false,
    enabledScheduleIds: []
  });
  const NESTED_DRAFT_KEY = 'treks:nested-return-draft';
  const lastLoadedEditRef = useRef(null);
  const slugAutoSourceNameRef = useRef('');
  const [eventCodeError, setEventCodeError] = useState('');

  const { data: categoryRes } = useQuery(['categories', 'trek'], () => categoryAPI.list({ type: 'trek' }));
  const categories = categoryRes?.data?.data || [];
  const { data: locationRes } = useQuery(['locations', 'active'], () => locationAPI.list({ isActive: true }));
  const locations = locationRes?.data?.data || [];
  const { data: eventTypeRes } = useQuery(['event-types', 'active'], () => eventTypeAPI.list({ isActive: true }));
  const { data: refundCategoryRes } = useQuery(['refund-calculator-categories', 'active'], () =>
    refundCalculatorCategoryAPI.list({ isActive: true })
  );
  const eventTypes = eventTypeRes?.data?.data || [];
  const refundCategories = refundCategoryRes?.data?.data || [];

  const { query, setQuery } = useListQueryParams({ page: '1', limit: '10', categoryId: '', search: '', edit: '' });
  const categoryFilter = query.categoryId || '';
  const searchFilter = query.search || '';
  const inventoryPage = Number(query.page || 1);
  const inventoryLimit = Number(query.limit || 10);
  const INVENTORY_PAGE_SIZE = 10;

  const { data: trekRes, isLoading } = useQuery(
    ['treks', categoryFilter, searchFilter, inventoryPage, inventoryLimit],
    () => trekAPI.list({
      categoryId: categoryFilter || undefined,
      search: searchFilter || undefined,
      page: inventoryPage,
      limit: inventoryLimit
    }),
    { keepPreviousData: true }
  );
  const treks = trekRes?.data?.data || [];
  const trekPagination = trekRes?.data?.pagination;
  const totalTreks = trekPagination?.total ?? treks.length;
  const activeCount = treks.filter((t) => t.status === 'active').length;
  const draftCount = treks.filter((t) => t.status === 'draft').length;
  const paginatedTreks = useMemo(() => treks, [treks]);
  const totalPages = trekPagination?.totalPages || Math.max(1, Math.ceil(totalTreks / INVENTORY_PAGE_SIZE));

  const { data: packageRes } = useQuery(['trek-packages', selectedTrekId], () => trekAPI.listPackages(selectedTrekId), { enabled: !!selectedTrekId });
  const { data: scheduleRes } = useQuery(['trek-schedule', selectedTrekId], () => trekAPI.listScheduleDays(selectedTrekId), { enabled: !!selectedTrekId });
  const { data: attractionRes } = useQuery(['trek-attractions', selectedTrekId], () => trekAPI.listAttractions(selectedTrekId), { enabled: !!selectedTrekId });
  const { data: feeRes } = useQuery(['trek-fees', selectedTrekId], () => trekAPI.listFeesIncludes(selectedTrekId), { enabled: !!selectedTrekId });
  const { data: instructionRes } = useQuery(['trek-instructions', selectedTrekId], () => trekAPI.listInstructions(selectedTrekId), { enabled: !!selectedTrekId });
  const { data: mediaRes } = useQuery(['trek-media', selectedTrekId], () => trekAPI.listMedia(selectedTrekId), { enabled: !!selectedTrekId });
  const { data: videosRes } = useQuery(['trek-videos', selectedTrekId], () => trekAPI.listVideos(selectedTrekId), { enabled: !!selectedTrekId });
  const { data: experienceGalleryRes } = useQuery(['trek-experience-gallery', selectedTrekId], () => trekAPI.listExperienceGallery(selectedTrekId), { enabled: !!selectedTrekId });

  const { data: upcomingForSimilarRes, isLoading: upcomingForSimilarLoading } = useQuery(
    ['event-batches-upcoming-treks-similar'],
    () => eventBatchAPI.listUpcomingTreks({ limit: 200 }),
    { enabled: showForm, staleTime: 60 * 1000 }
  );

  const similarAdventureOptions = useMemo(() => {
    const currentIdRaw = editing ?? selectedTrekId;
    const currentId = currentIdRaw != null && currentIdRaw !== '' ? Number(currentIdRaw) : null;
    const rows = upcomingForSimilarRes?.data?.data || [];
    const list = rows
      .map((row) => row.trek)
      .filter((t) => t?.id && t?.name && (currentId == null || Number.isNaN(currentId) || Number(t.id) !== currentId));
    const byId = new Map(list.map((t) => [Number(t.id), { id: t.id, name: t.name }]));
    const selected = Array.isArray(form.similarTrekIds) ? form.similarTrekIds : [];
    for (const rawId of selected) {
      const id = Number(rawId);
      if (!id || id === currentId) continue;
      if (!byId.has(id)) {
        byId.set(id, {
          id,
          name: `Trek #${id} (no upcoming batches — uncheck to remove)`
        });
      }
    }
    return Array.from(byId.values()).sort((a, b) =>
      String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' })
    );
  }, [upcomingForSimilarRes, editing, selectedTrekId, form.similarTrekIds]);

  const packages = packageRes?.data?.data || [];
  const scheduleDays = scheduleRes?.data?.data || [];
  const attractions = attractionRes?.data?.data || [];
  const feesIncludes = feeRes?.data?.data || [];
  const instructions = instructionRes?.data?.data || [];
  const media = mediaRes?.data?.data || [];
  const sortedMedia = useMemo(() => {
    const m = media || [];
    return [...m].sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
  }, [media]);

  const closeTrekEditor = useCallback(() => {
    setEditing(null);
    setForm(emptyForm);
    setSelectedTrekId(null);
    setShowForm(false);
    setTrekEditorLoading(false);
    setEventCodeError('');
    slugAutoSourceNameRef.current = '';
    quickPolicyContentBackupStore.clear();
    for (const key of Object.keys(quickPolicyEditorRefStore)) delete quickPolicyEditorRefStore[key];
    lastLoadedEditRef.current = null;
    setRichTextMountKey((k) => k + 1);
    setQuery({ edit: '' }, { resetPage: false });
  }, [setQuery]);

  const hydrateFormFromTrekItem = useCallback((rawItem) => {
    const item = unwrapTrekApiRow(rawItem);
    if (!item) return;
    const quickPolicyRows = deriveQuickPolicyLinksForForm(item);
    const quickPolicyBackup = new Map();
    quickPolicyRows.forEach((row) => {
      const html = String(row.content || '').trim();
      if (html) quickPolicyBackup.set(row.id, html);
    });
    quickPolicyContentBackupStore.clear();
    quickPolicyBackup.forEach((html, id) => quickPolicyContentBackupStore.set(id, html));
    const {
      isHighlighted: _ignoreHighlighted,
      isActive: _ignoreActive,
      thingsToCarry: _legacyThings,
      cancellationPolicy: _legacyCancel,
      termsAndConditions: _legacyTerms,
      quickPolicyLinks: _qplSpread,
      packages: _packages,
      scheduleDays: _scheduleDays,
      attractions: _attractions,
      feesIncludes: _feesIncludes,
      instructions: _instructions,
      media: _media,
      videos: _videos,
      experienceGallery: _experienceGallery,
      batches: _batches,
      similarTreks: _similarTreks,
      category: _category,
      locationMaster: _locationMaster,
      eventType: _eventType,
      consultant: _consultant,
      leadTemplates: _leadTemplates,
      ...itemRest
    } = item;

    const faqsRaw = item.faqs;
    let faqsForForm = '[]';
    if (faqsRaw == null) faqsForForm = '[]';
    else if (typeof faqsRaw === 'string') faqsForForm = faqsRaw.trim() || '[]';
    else if (Array.isArray(faqsRaw)) {
      try {
        faqsForForm = JSON.stringify(faqsRaw);
      } catch {
        faqsForForm = '[]';
      }
    }

    setForm({
      ...emptyForm,
      ...itemRest,
      status: normalizeTrekStatusFromApi(item),
      advancePaymentEnabled: item.advancePaymentEnabled ?? item.advance_payment_enabled ?? true,
      lengthKm: item.lengthKm ?? '',
      minAge: item.minAge ?? '',
      maxAge: item.maxAge ?? '',
      description: String(
        item.description ?? item.about ?? item.about_html ?? item.aboutHtml ?? ''
      ),
      importantUpdate: String(item.importantUpdate ?? item.important_update ?? ''),
      faqs: faqsForForm,
      similarTrekIds: Array.isArray(item.similarTrekIds) ? [...item.similarTrekIds] : [],
      stayOptions: parseJsonArrayField(item.stayOptions ?? item.stay_options).map((o) => ({
        id: o.id || '',
        name: o.name || '',
        priceAdjust: o.priceAdjust != null ? o.priceAdjust : 0
      })),
      stayDetails: parseJsonArrayField(item.stayDetails ?? item.stay_details).map((s) => ({
        name: s.name || '',
        stayType: s.stayType || '',
        imageUrl: s.imageUrl || '',
        durationLabel: s.durationLabel || '',
        ratingLabel: s.ratingLabel || '',
        galleryUrl: s.galleryUrl || '',
        features: parseJsonArrayField(s.features).map((f) => ({ icon: f.icon || '', text: f.text || '' }))
      })),
      badges: parseJsonArrayField(item.badges).map((b) => ({
        label: b.label || b.text || '',
        icon: b.icon || ''
      })),
      refundCalculatorCategoryId: String(
        item.refundCalculatorCategoryId ?? item.refund_calculator_category_id ?? ''
      ),
      eventTypeIds: (() => {
        const raw = item.eventTypeIds ?? item.event_type_ids;
        if (Array.isArray(raw) && raw.length) return raw.map((id) => String(id));
        if (item.eventTypeId != null && item.eventTypeId !== '') return [String(item.eventTypeId)];
        return [];
      })(),
      leadTemplates: item?.leadTemplates ?? item?.lead_templates ?? null,
      quickPolicyLinks: quickPolicyRows,
      logisticsIcons: mergeLogisticsIconsFromApi(item.logisticsIcons ?? item.logistics_icons)
    });
    slugAutoSourceNameRef.current = String(item.name || '').trim();
    setEventCodeError('');
  }, []);

  const loadTrekEditor = useCallback(
    async (editId) => {
      const id = String(editId || '').trim();
      if (!id) return;

      setEditing(id);
      setSelectedTrekId(id);
      setShowForm(true);
      setTrekEditorLoading(true);

      const listRow = treks.find((t) => String(t.id) === id);
      if (listRow) {
        try {
          hydrateFormFromTrekItem(listRow);
        } catch {
          /* list row hydrate is best-effort */
        }
      }

      try {
        const res = await trekAPI.get(id);
        const full = unwrapTrekApiRow(res?.data?.data);
        if (full) {
          hydrateFormFromTrekItem(full);
          lastLoadedEditRef.current = id;
        } else {
          showToast({ message: 'Trek not found on server.', severity: 'error' });
        }
      } catch (error) {
        showToast({
          message:
            error?.response?.data?.message ||
            'Could not load trek details. Check Network for GET /treks/{id} (status should be 200).',
          severity: 'error'
        });
      } finally {
        setTrekEditorLoading(false);
        setRichTextMountKey((k) => k + 1);
      }
    },
    [hydrateFormFromTrekItem, showToast, treks]
  );

  const trekCardPreviewMeta = useMemo(() => {
    const li = form.logisticsIcons || {};
    const lenSlug = String(li.lengthKm || '').trim() || 'route';
    const diffSlug = String(li.difficulty || '').trim() || 'mountain';
    const kmRaw = form.lengthKm;
    const kmNum = kmRaw !== '' && kmRaw != null ? Number(kmRaw) : NaN;
    const kmText = Number.isFinite(kmNum) ? `${kmNum} km` : `${kmRaw ?? 0} km`;
    return {
      LengthIcon: getIncludeSidebarLucideComponent(lenSlug),
      DifficultyIcon: getIncludeSidebarLucideComponent(diffSlug),
      kmText,
      difficultyText: form.difficulty || 'Difficulty'
    };
  }, [form.logisticsIcons, form.lengthKm, form.difficulty]);

  const videos = videosRes?.data?.data || [];
  const experienceGallery = experienceGalleryRes?.data?.data || [];

  const extractYouTubeVideoId = (input) => {
    const value = (input || '').trim();
    if (!value) return '';
    const directIdPattern = /^[a-zA-Z0-9_-]{11}$/;
    if (directIdPattern.test(value)) return value;
    try {
      const url = new URL(value);
      if (url.hostname.includes('youtu.be')) {
        const id = url.pathname.replace('/', '');
        return directIdPattern.test(id) ? id : '';
      }
      if (url.hostname.includes('youtube.com')) {
        const idFromQuery = url.searchParams.get('v');
        if (directIdPattern.test(idFromQuery || '')) return idFromQuery;
        const parts = url.pathname.split('/').filter(Boolean);
        const fallback = parts[parts.length - 1] || '';
        return directIdPattern.test(fallback) ? fallback : '';
      }
      return '';
    } catch {
      return '';
    }
  };

  const resolveImageUrl = async (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (value instanceof File) {
      const response = await trekAPI.uploadImage(value);
      const uploadedUrl = response?.data?.data?.url;
      if (!uploadedUrl) {
        throw new Error('Image upload failed');
      }
      return uploadedUrl;
    }
    return '';
  };

  const onUploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      setIsImageUploading(true);
      const trekId = await ensureTrekId();
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const url = await resolveImageUrl(file);
        const title = file.name.replace(/\.[^/.]+$/, '');
        const order = (media?.length || 0) + index;
        // eslint-disable-next-line no-await-in-loop
        await trekAPI.createMedia(trekId, { mediaType: 'image', url, title, order });
      }
      invalidateNested();
    } catch (error) {
      showToast({
        message: error?.response?.data?.message || 'Failed to upload images.',
        severity: 'error'
      });
    } finally {
      setIsImageUploading(false);
      e.target.value = '';
    }
  };

  const onDeleteImage = async (id) => {
    try {
      const trekId = await ensureTrekId();
      await trekAPI.deleteMedia(trekId, id);
      invalidateNested();
    } catch (error) {
      showToast({
        message: error?.response?.data?.message || 'Failed to delete image.',
        severity: 'error'
      });
    }
  };

  const onAddExperienceGalleryPhoto = async (source) => {
    const trekId = await ensureTrekId();
    try {
      setIsExpGalleryAdding(true);
      let url = '';
      if (source === 'file' && expGalleryPhotoInputRef.current?.files?.[0]) {
        url = await resolveImageUrl(expGalleryPhotoInputRef.current.files[0]);
      } else if (source === 'url' && expGalleryPhotoUrl?.trim()) {
        url = expGalleryPhotoUrl.trim();
      }
      if (!url) {
        if (source === 'url') showToast({ message: 'Enter an image URL.', severity: 'warning' });
        return;
      }
      const order = (experienceGallery?.length || 0);
      await trekAPI.createExperienceGalleryItem(trekId, { type: 'image', url, order });
      setExpGalleryPhotoUrl('');
      if (expGalleryPhotoInputRef.current) expGalleryPhotoInputRef.current.value = '';
      invalidateNested();
      showToast({ message: 'Photo added to Experience Gallery.', severity: 'success' });
    } catch (error) {
      showToast({ message: error?.response?.data?.message || 'Failed to add photo.', severity: 'error' });
    } finally {
      setIsExpGalleryAdding(false);
    }
  };

  const onAddExperienceGalleryVideo = async () => {
    const videoId = extractYouTubeVideoId(expGalleryVideoUrl);
    if (!videoId) {
      showToast({ message: 'Enter a valid YouTube video URL.', severity: 'warning' });
      return;
    }
    try {
      setIsExpGalleryAdding(true);
      const trekId = await ensureTrekId();
      const order = (experienceGallery?.length || 0);
      await trekAPI.createExperienceGalleryItem(trekId, { type: 'video', videoId, order });
      setExpGalleryVideoUrl('');
      invalidateNested();
      showToast({ message: 'Video added to Experience Gallery.', severity: 'success' });
    } catch (error) {
      showToast({ message: error?.response?.data?.message || 'Failed to add video.', severity: 'error' });
    } finally {
      setIsExpGalleryAdding(false);
    }
  };

  const onDeleteExperienceGalleryItem = async (id) => {
    try {
      const trekId = await ensureTrekId();
      await trekAPI.deleteExperienceGalleryItem(trekId, id);
      invalidateNested();
      showToast({ message: 'Removed from Experience Gallery.', severity: 'success' });
    } catch (error) {
      showToast({ message: error?.response?.data?.message || 'Failed to remove.', severity: 'error' });
    }
  };

  const onPickExperienceGalleryVideoThumbnail = (itemId) => {
    setExpGalleryVideoThumbItemId(itemId);
    expGalleryVideoThumbInputRef.current?.click();
  };

  const onExperienceGalleryVideoThumbnailFile = async (e) => {
    const file = e.target.files?.[0];
    const itemId = expGalleryVideoThumbItemId;
    if (expGalleryVideoThumbInputRef.current) expGalleryVideoThumbInputRef.current.value = '';
    setExpGalleryVideoThumbItemId(null);
    if (!file || !itemId) return;
    try {
      setIsExpGalleryThumbUploading(true);
      const trekId = await ensureTrekId();
      const url = await resolveImageUrl(file);
      await trekAPI.updateExperienceGalleryItem(trekId, itemId, { thumbnailUrl: url });
      invalidateNested();
      showToast({ message: 'Video poster / thumbnail updated.', severity: 'success' });
    } catch (error) {
      showToast({
        message: error?.response?.data?.message || 'Failed to upload thumbnail. If this persists, run the DB migration for thumbnail_url on trek_experience_gallery.',
        severity: 'error'
      });
    } finally {
      setIsExpGalleryThumbUploading(false);
    }
  };

  const onClearExperienceGalleryVideoThumbnail = async (itemId) => {
    try {
      const trekId = await ensureTrekId();
      await trekAPI.updateExperienceGalleryItem(trekId, itemId, { thumbnailUrl: null });
      invalidateNested();
      showToast({ message: 'Poster cleared; YouTube default thumbnail is used.', severity: 'success' });
    } catch (error) {
      showToast({ message: error?.response?.data?.message || 'Failed to clear poster.', severity: 'error' });
    }
  };

  const resetPackageForm = () => {
    setEditingPackageId(null);
    setPackageForm({
      name: '',
      description: '',
      price: '',
      vacancy: '',
      startDayOffset: 0,
      endDayOffset: 0,
      priority: 0,
      imageUrl: '',
      reportingMessage: '',
      ticketRequired: false,
      ticketDeparture: false,
      ticketDepartureAddon: false,
      ticketReturn: false,
      ticketReturnAddon: false,
      hidden: false,
      enabledScheduleIds: []
    });
  };

  const handleAddPackageClick = async () => {
    try {
      const trekId = await ensureTrekId();
      persistDraftForNestedNavigation(trekId);
      navigate(`/treks/${trekId}/packages/new`);
    } catch {
      // ensureTrekId already surfaced validation error
    }
  };

  const handleEditPackageClick = async (pkg) => {
    try {
      const trekId = await ensureTrekId();
      persistDraftForNestedNavigation(trekId);
      window.open(`/treks/${trekId}/packages/${pkg.id}`, '_blank');
    } catch {
      // ignore
    }
  };

  const handleSubmitPackage = async (e) => {
    e.preventDefault();
    try {
      const trekId = await ensureTrekId();
      const payload = {
        name: packageForm.name.trim(),
        description: packageForm.description.trim(),
        price: packageForm.price ? Number(packageForm.price) : null,
        vacancy: packageForm.vacancy ? Number(packageForm.vacancy) : null,
        defaultVacancy: packageForm.vacancy ? Number(packageForm.vacancy) : null,
        ...packageDayPayloadFromForm(packageForm),
        priority: Number(packageForm.priority || 0),
        imageUrl: packageForm.imageUrl || null,
        image: packageForm.imageUrl || null,
        reportingMessage: packageForm.reportingMessage || '',
        ticketRequired: Boolean(packageForm.ticketRequired),
        ticketDeparture: Boolean(packageForm.ticketDeparture),
        ticketDepartureAddon: Boolean(packageForm.ticketDepartureAddon),
        ticketReturn: Boolean(packageForm.ticketReturn),
        ticketReturnAddon: Boolean(packageForm.ticketReturnAddon),
        hidden: Boolean(packageForm.hidden),
        enabledScheduleIds: packageForm.enabledScheduleIds || []
      };
      if (!payload.name) {
        showToast({
          message: 'Package Name is required.',
          severity: 'warning'
        });
        return;
      }
      if (editingPackageId) {
        const res = await trekAPI.updatePackage(trekId, editingPackageId, payload);
        const priceSync = res?.data?.priceSync;
        const repriced = Number(priceSync?.updated || 0);
        showToast({
          message:
            repriced > 0
              ? res?.data?.message || `Package updated. Repriced ${repriced} not-fully-paid booking(s).`
              : 'Package updated successfully.',
          severity: 'success'
        });
      } else {
        await trekAPI.createPackage(trekId, payload);
        showToast({ message: 'Package created successfully.', severity: 'success' });
      }
      resetPackageForm();
      setShowPackageForm(false);
      invalidateNested();
    } catch (error) {
      if (error?.message === 'Validation failed') return;
      showToast({
        message: error?.response?.data?.message || 'Failed to save package.',
        severity: 'error'
      });
    }
  };

  const handleDeletePackage = async (id) => {
    try {
      const trekId = await ensureTrekId();
      await trekAPI.deletePackage(trekId, id);
      invalidateNested();
    } catch (error) {
      if (error?.message === 'Validation failed') return;
      showToast({
        message: error?.response?.data?.message || 'Failed to delete package.',
        severity: 'error'
      });
    }
  };

  const handleAddAttractionClick = async () => {
    try {
      const trekId = await ensureTrekId();
      navigate(`/treks/${trekId}/attractions/new`);
    } catch {
      // ensureTrekId already surfaced validation error
    }
  };

  const handleEditAttractionClick = async (attraction) => {
    try {
      const trekId = await ensureTrekId();
      window.open(`/treks/${trekId}/attractions/${attraction.id}`, '_blank');
    } catch {
      // ignore
    }
  };

  const handleDeleteAttraction = async (id) => {
    try {
      const trekId = await ensureTrekId();
      await trekAPI.deleteAttraction(trekId, id);
      invalidateNested();
    } catch (error) {
      if (error?.message === 'Validation failed') return;
      showToast({
        message: error?.response?.data?.message || 'Failed to delete attraction.',
        severity: 'error'
      });
    }
  };

  const handleAddFeeIncludeClick = async () => {
    try {
      const trekId = await ensureTrekId();
      navigate(`/treks/${trekId}/fees-includes/new`);
    } catch {
      // ensureTrekId already surfaced validation error
    }
  };

  const handleEditFeeIncludeClick = async (fee) => {
    try {
      const trekId = await ensureTrekId();
      window.open(`/treks/${trekId}/fees-includes/${fee.id}`, '_blank');
    } catch {
      // ignore
    }
  };

  const handleDeleteFeeInclude = async (id) => {
    try {
      const trekId = await ensureTrekId();
      await trekAPI.deleteFeeInclude(trekId, id);
      invalidateNested();
    } catch (error) {
      if (error?.message === 'Validation failed') return;
      showToast({
        message: error?.response?.data?.message || 'Failed to delete Fees Includes item.',
        severity: 'error'
      });
    }
  };

  const handleAddInstructionClick = async () => {
    try {
      const trekId = await ensureTrekId();
      navigate(`/treks/${trekId}/instructions/new`);
    } catch {
      // ensureTrekId already surfaced validation error
    }
  };

  const handleEditInstructionClick = async (instruction) => {
    try {
      const trekId = await ensureTrekId();
      window.open(`/treks/${trekId}/instructions/${instruction.id}`, '_blank');
    } catch {
      // ignore
    }
  };

  const handleDeleteInstruction = async (id) => {
    try {
      const trekId = await ensureTrekId();
      await trekAPI.deleteInstruction(trekId, id);
      invalidateNested();
    } catch (error) {
      if (error?.message === 'Validation failed') return;
      showToast({
        message: error?.response?.data?.message || 'Failed to delete instruction.',
        severity: 'error'
      });
    }
  };

  const toggleScheduleForPackage = (dayId, enable) => {
    setPackageForm((prev) => {
      const current = new Set(prev.enabledScheduleIds || []);
      if (enable) current.add(dayId);
      else current.delete(dayId);
      return { ...prev, enabledScheduleIds: Array.from(current) };
    });
  };

  const handleUploadPackageImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setIsPackageImageUploading(true);
      const response = await trekAPI.uploadImage(file);
      const url = response?.data?.data?.url;
      if (!url) throw new Error('Upload failed');
      setPackageForm((prev) => ({ ...prev, imageUrl: url }));
    } catch (error) {
      showToast({
        message: error?.response?.data?.message || 'Failed to upload package image.',
        severity: 'error'
      });
    } finally {
      setIsPackageImageUploading(false);
      e.target.value = '';
    }
  };

  const handleUploadBrochure = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setIsBrochureUploading(true);
      const brochureName = String(form.slug || form.name || '').trim();
      const response = await adminAPI.uploadPdf(file, { name: brochureName || undefined });
      const url = response?.data?.data?.url;
      if (!url) throw new Error('Upload failed');
      setForm((prev) => ({ ...prev, brochureUrl: url }));
      showToast({
        message: 'Brochure PDF uploaded successfully.',
        severity: 'success'
      });
    } catch (error) {
      showToast({
        message: error?.response?.data?.message || 'Failed to upload brochure PDF.',
        severity: 'error'
      });
    } finally {
      setIsBrochureUploading(false);
      e.target.value = '';
    }
  };

  const handleUploadTips = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setIsTipsUploading(true);
      const response = await adminAPI.uploadTipsAsset(file);
      const url = response?.data?.data?.url;
      if (!url) throw new Error('Upload failed');
      setForm((prev) => ({ ...prev, tipsUrl: url }));
      showToast({
        message: 'Tips file uploaded successfully.',
        severity: 'success'
      });
    } catch (error) {
      showToast({
        message: error?.response?.data?.message || error?.message || 'Failed to upload tips file.',
        severity: 'error'
      });
    } finally {
      setIsTipsUploading(false);
      e.target.value = '';
    }
  };

  const invalidateNested = () => {
    queryClient.invalidateQueries(['trek-packages', selectedTrekId]);
    queryClient.invalidateQueries(['trek-schedule', selectedTrekId]);
    queryClient.invalidateQueries(['trek-attractions', selectedTrekId]);
    queryClient.invalidateQueries(['trek-fees', selectedTrekId]);
    queryClient.invalidateQueries(['trek-instructions', selectedTrekId]);
    queryClient.invalidateQueries(['trek-media', selectedTrekId]);
    queryClient.invalidateQueries(['trek-videos', selectedTrekId]);
    queryClient.invalidateQueries(['trek-experience-gallery', selectedTrekId]);
  };

  const onReorderTrekMedia = async (draggedId, targetId) => {
    if (!draggedId || !targetId || draggedId === targetId || !selectedTrekId) return;
    const list = [...sortedMedia];
    const from = list.findIndex((m) => String(m.id) === String(draggedId));
    const to = list.findIndex((m) => String(m.id) === String(targetId));
    if (from < 0 || to < 0) return;
    const [row] = list.splice(from, 1);
    list.splice(to, 0, row);
    try {
      setIsReorderingMedia(true);
      await Promise.all(
        list.map((item, idx) => trekAPI.updateMedia(selectedTrekId, item.id, { order: idx }))
      );
      invalidateNested();
    } catch (error) {
      showToast({
        message: error?.response?.data?.message || 'Failed to reorder images.',
        severity: 'error'
      });
    } finally {
      setIsReorderingMedia(false);
      setMediaDragId(null);
    }
  };

  const createMutation = useMutation((payload) => trekAPI.create(payload), {
    onSuccess: (response) => {
      queryClient.invalidateQueries(['treks']);
      const createdId = response?.data?.data?.id;
      if (createdId) {
        setEditing(createdId);
        setSelectedTrekId(createdId);
        showToast({
          message: 'Event created successfully.',
          severity: 'success'
        });
      }
    }
  });
  const updateMutation = useMutation(({ id, payload }) => trekAPI.update(id, payload), {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['treks']);
      const p = variables?.payload || {};
      const keys = Object.keys(p);
      const statusOnly =
        keys.length === 2 && keys.includes('status') && keys.includes('isActive');
      if (statusOnly) {
        showToast({
          message: p.status === 'active' ? 'Trek is now active.' : 'Trek is now inactive.',
          severity: 'success'
        });
        return;
      }
      const published = p.status === 'active';
      showToast({
        message: published
          ? 'Event published successfully. It will appear on the website once it has at least one event batch (with start/end dates).'
          : 'Event updated successfully.',
        severity: 'success'
      });
      trekBaselineRef.current = trekPayloadFingerprint(p);
      initialStatusRef.current = String(p.status || 'active').toLowerCase();
    },
    onError: (error) => {
      showToast({
        message: error?.response?.data?.message || 'Failed to save event.',
        severity: 'error'
      });
    }
  });
  const deleteMutation = useMutation((id) => trekAPI.remove(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(['treks']);
      showToast({
        message: 'Event deleted successfully.',
        severity: 'success'
      });
    },
    onError: (error) => {
      showToast({
        message: error?.response?.data?.message || 'Failed to delete event.',
        severity: 'error'
      });
    }
  });

  const checkEventCodeUnique = async (code) => {
    const trimmed = String(code || '').trim();
    if (!trimmed) {
      setEventCodeError('');
      return { ok: true, message: '' };
    }
    try {
      const res = await trekAPI.list({ search: trimmed, limit: 50 });
      const rows = res?.data?.data || [];
      const clash = rows.find(
        (t) =>
          String(t.code || '').trim().toLowerCase() === trimmed.toLowerCase() &&
          String(t.id) !== String(editing || '')
      );
      if (clash) {
        const message = `Event code "${trimmed}" is already used by "${clash.name}".`;
        setEventCodeError(message);
        return { ok: false, message };
      }
      setEventCodeError('');
      return { ok: true, message: '' };
    } catch {
      setEventCodeError('');
      return { ok: true, message: '' };
    }
  };

  const handleEventNameChange = (name) => {
    setForm((prev) => {
      const next = { ...prev, name };
      const prevAutoSlug = slugifyTrekName(slugAutoSourceNameRef.current);
      const currentSlug = String(prev.slug || '').trim();
      const shouldAutoSlug =
        !canEditSlugs ||
        !currentSlug ||
        currentSlug === prevAutoSlug ||
        currentSlug === slugifyTrekName(prev.name);
      if (shouldAutoSlug) {
        next.slug = slugifyTrekName(name);
        slugAutoSourceNameRef.current = name;
      }
      return next;
    });
  };

  const validateTrekForm = async () => {
    const errors = [];
    if (!form.name || !form.name.trim()) errors.push('Event Name is required.');
    const slug = (form.slug && form.slug.trim()) || slugifyTrekName(form.name);
    if (!slug) errors.push('Slug could not be generated from event name.');
    if (errors.length) {
      showToast({
        message: errors.join('\n'),
        severity: 'warning'
      });
      return false;
    }
    const codeCheck = await checkEventCodeUnique(form.code);
    if (!codeCheck.ok) {
      showToast({
        message: codeCheck.message || 'Event code must be unique.',
        severity: 'warning'
      });
      return false;
    }
    return true;
  };

  const buildTrekPayload = (statusOverride, formSource = form) => {
    const payload = {
      ...formSource,
      status: statusOverride || formSource.status || 'active',
      lengthKm: formSource.lengthKm ? Number(formSource.lengthKm) : null,
      minAge: formSource.minAge ? Number(formSource.minAge) : null,
      maxAge: formSource.maxAge ? Number(formSource.maxAge) : null,
      order: Number(formSource.order || 0)
    };
    payload.isActive = payload.status === 'active';
    payload.advancePaymentEnabled = formSource.advancePaymentEnabled !== false;

    // Normalize fields so DB enums/UUIDs don't receive empty strings
    if (!payload.campsiteId) payload.campsiteId = null;
    if (!payload.categoryId) payload.categoryId = null;
    if (!payload.locationId) payload.locationId = null;
    if (!payload.refundCalculatorCategoryId) payload.refundCalculatorCategoryId = null;
    else payload.refundCalculatorCategoryId = Number(payload.refundCalculatorCategoryId) || null;
    const typeIds = Array.isArray(formSource.eventTypeIds)
      ? [...new Set(formSource.eventTypeIds.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0))]
      : [];
    payload.eventTypeIds = typeIds;
    payload.eventTypeId = typeIds.length ? typeIds[0] : null;
    if (!payload.difficulty) payload.difficulty = null;

    const li = formSource.logisticsIcons || {};
    const logisticsIcons = {};
    for (const k of ['duration', 'lengthKm', 'age', 'difficulty', 'altitude']) {
      const v = String(li[k] ?? '').trim();
      if (v) logisticsIcons[k] = v;
    }
    payload.logisticsIcons = logisticsIcons;
    // code is unique; empty string would violate if another trek has ''
    if (payload.code !== undefined && !String(payload.code).trim()) payload.code = null;
    if (!payload.slug || !String(payload.slug).trim()) {
      payload.slug = slugifyTrekName(payload.name) || payload.slug;
    }

    payload.similarTrekIds = Array.isArray(formSource.similarTrekIds) ? formSource.similarTrekIds.filter(Boolean) : [];
    payload.stayOptions = Array.isArray(formSource.stayOptions)
      ? formSource.stayOptions
          .filter((o) => o && (o.name || o.id))
          .map((o) => ({
            id: String(o.id || '').trim() || `stay-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            name: String(o.name || '').trim() || o.id || 'Option',
            priceAdjust: Number(o.priceAdjust) || 0
          }))
      : [];

    payload.stayDetails = Array.isArray(formSource.stayDetails)
      ? formSource.stayDetails
          .filter((s) => s && (s.name || s.durationLabel))
          .map((s) => ({
            name: String(s.name || '').trim() || 'Stay',
            stayType: String(s.stayType || '').trim() || null,
            imageUrl: String(s.imageUrl || '').trim() || null,
            durationLabel: String(s.durationLabel || '').trim() || null,
            ratingLabel: String(s.ratingLabel || '').trim() || null,
            galleryUrl: String(s.galleryUrl || '').trim() || null,
            features: Array.isArray(s.features) ? s.features.filter((f) => f && (f.icon || f.text)).map((f) => ({ icon: String(f.icon || '').trim() || 'check', text: String(f.text || '').trim() || '' })) : []
          }))
      : [];

    payload.badges = Array.isArray(formSource.badges)
      ? formSource.badges
          .filter((b) => b && (b.label || b.text))
          .map((b) => ({
            label: String(b.label || b.text || '').trim(),
            icon: String(b.icon || '').trim() || null
          }))
      : [];

    payload.description = normalizeQuillHtml(formSource.description);
    payload.importantUpdate = normalizeQuillHtml(formSource.importantUpdate);

    const qlRaw = Array.isArray(formSource.quickPolicyLinks) ? formSource.quickPolicyLinks : [];
    payload.quickPolicyLinks = qlRaw
      .map((x) => {
        const rowId = String(x.id || '').trim() || newQuickPolicyRowId();
        const editorRef = quickPolicyEditorRefStore[rowId];
        const liveHtml = editorRef?.getHtml?.() ?? '';
        const content = resolvePolicyRichHtmlForSave({
          liveHtml,
          formHtml: x.content,
          backupHtml: quickPolicyContentBackupStore.get(rowId)
        });
        return {
          id: rowId,
          icon: String(x.icon || 'file-text').trim() || 'file-text',
          title: String(x.title || '').trim() || 'Untitled',
          content
        };
      })
      .filter((x) => x && (String(x.title || '').trim() || x.content));

    payload.thingsToCarry = null;
    payload.cancellationPolicy = null;
    payload.termsAndConditions = null;
    // Preserve leadTemplates configured via the centralized WhatsApp Automation page
    if (formSource.leadTemplates !== undefined) {
      payload.leadTemplates = formSource.leadTemplates;
    }

    try {
      const parsed = typeof formSource.faqs === 'string' ? JSON.parse(formSource.faqs || '[]') : formSource.faqs;
      if (Array.isArray(parsed)) {
        payload.faqs = JSON.stringify(
          parsed.map((f) => ({
            q: String(f?.q || '').trim(),
            a: normalizeQuillHtml(f?.a)
          }))
        );
      }
    } catch {
      /* keep form.faqs as-is */
    }

    delete payload.isHighlighted;
    for (const key of TREK_FORM_READONLY_KEYS) {
      delete payload[key];
    }

    return payload;
  };

  const isTrekFormDirty = () => {
    if (!trekBaselineRef.current) return true;
    return trekPayloadFingerprint(buildTrekPayload(undefined, form)) !== trekBaselineRef.current;
  };

  const ensureTrekId = async () => {
    const existingId = selectedTrekId || editing;
    if (existingId) return existingId;
    // When adding nested items (packages, schedule, attractions, fees, etc.), allow creating
    // a draft trek with placeholders so we don't block on "Event Name / Slug required"
    const payload = buildTrekPayload('draft');
    const name = (payload.name && String(payload.name).trim()) || 'New Trek';
    const slug = (payload.slug && String(payload.slug).trim()) || `new-trek-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    payload.name = name;
    payload.slug = slug;
    const response = await trekAPI.create(payload);
    const createdId = response?.data?.data?.id;
    if (!createdId) {
      throw new Error('Failed to create trek');
    }
    setEditing(createdId);
    setSelectedTrekId(createdId);
    setForm((prev) => ({ ...prev, name, slug }));
    queryClient.invalidateQueries(['treks']);
    return createdId;
  };

  const persistDraftForNestedNavigation = (trekId) => {
    try {
      const safeForm = { ...form };
      if (typeof safeForm.mainImage !== 'string') safeForm.mainImage = '';
      if (typeof safeForm.brochurePdf !== 'string') safeForm.brochurePdf = '';
      if (typeof safeForm.tipsPdf !== 'string') safeForm.tipsPdf = '';
      sessionStorage.setItem(
        NESTED_DRAFT_KEY,
        JSON.stringify({
          trekId: String(trekId),
          editing: editing || trekId,
          selectedTrekId: trekId,
          form: safeForm
        })
      );
    } catch {
      // non-blocking: nested navigation should still work
    }
  };

  const submitTrek = async (statusOverride) => {
    if (!(await validateTrekForm())) return;
    const dirty = isTrekFormDirty();

    if (editing && !dirty && statusOverride !== 'active') {
      showToast({ message: 'No changes to save.', severity: 'info' });
      return;
    }

    let resolvedStatus = statusOverride;
    if (statusOverride === 'draft') {
      resolvedStatus = 'draft';
    } else if (statusOverride === 'active') {
      resolvedStatus = 'active';
    } else {
      resolvedStatus = initialStatusRef.current || form.status || 'active';
    }

    const payload = buildTrekPayload(resolvedStatus);
    if (editing) updateMutation.mutate({ id: editing, payload });
    else createMutation.mutate(payload);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    submitTrek();
  };

  const onEdit = (item) => {
    if (!item?.id) return;
    lastLoadedEditRef.current = null;
    setQuery({ edit: String(item.id) }, { resetPage: false });
  };

  /* Open editor when URL has ?edit=123 (Edit click, refresh, return from nested form). */
  useEffect(() => {
    const editId = String(query.edit || '').trim();
    if (!editId) return;
    if (lastLoadedEditRef.current === editId) return;
    loadTrekEditor(editId);
  }, [query.edit, loadTrekEditor]);

  /* Editor open with trek id in state but missing ?edit= in URL — sync so refresh works. */
  useEffect(() => {
    if (!showForm || !editing) return;
    const editId = String(editing);
    if (String(query.edit || '').trim() === editId) return;
    setQuery({ edit: editId }, { resetPage: false });
  }, [showForm, editing, query.edit, setQuery]);

  /* Baseline after list/GET hydrate + rich editors mount — avoids treating open-only as a draft save */
  useEffect(() => {
    if (!showForm || trekEditorLoading) return;
    const t = window.setTimeout(() => {
      trekBaselineRef.current = trekPayloadFingerprint(buildTrekPayload(undefined, form));
      initialStatusRef.current = normalizeTrekStatusFromApi({ status: form.status });
    }, 400);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- baseline only when editor opens/loads, not on each keystroke
  }, [showForm, trekEditorLoading, editing, richTextMountKey]);

  /* Push saved policy HTML into Quill after trek load (Quill 2 often clears legacy HTML on first mount). */
  useEffect(() => {
    if (!showForm || trekEditorLoading) return;
    const pushBackupIntoEditors = () => {
      for (const [id, html] of quickPolicyContentBackupStore.entries()) {
        if (!htmlHasRichContent(html)) continue;
        quickPolicyEditorRefStore[id]?.applyHtml?.(html);
      }
    };
    const delays = [0, 120, 300, 600, 1000];
    const timers = delays.map((ms) => window.setTimeout(pushBackupIntoEditors, ms));
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [showForm, trekEditorLoading, richTextMountKey]);

  /* Browser tab: show trek name while editor is open */
  useEffect(() => {
    if (!showForm) return undefined;
    const name = String(form.name || '').trim();
    const prev = document.title;
    if (name) document.title = name;
    return () => {
      document.title = prev;
    };
  }, [showForm, form.name]);

  /* When returning from a nested form (package, schedule, etc.), reopen the same trek in edit mode */
  useEffect(() => {
    const openTrekId = location.state?.openTrekId;
    if (!openTrekId) return;
    const openId = String(openTrekId);
    let restored = false;
    try {
      const rawDraft = sessionStorage.getItem(NESTED_DRAFT_KEY);
      if (rawDraft) {
        const draft = JSON.parse(rawDraft);
        if (String(draft?.trekId || '') === openId && draft?.form && typeof draft.form === 'object') {
          setSelectedTrekId(openTrekId);
          setEditing(draft.editing || openTrekId);
          setShowForm(true);
          setForm((prev) => ({ ...prev, ...draft.form }));
          restored = true;
        }
      }
    } catch {
      // ignore parse errors and fallback to server copy
    } finally {
      sessionStorage.removeItem(NESTED_DRAFT_KEY);
    }
    setQuery({ edit: openId }, { resetPage: false });
    if (restored) {
      setRichTextMountKey((k) => k + 1);
    }
    const nextSearch = new URLSearchParams(window.location.search);
    nextSearch.set('edit', openId);
    navigate({ pathname: '/treks', search: nextSearch.toString() }, { replace: true, state: {} });
  }, [location.state?.openTrekId, navigate, setQuery]);

  /* Close Similar Adventures dropdown when clicking outside */
  useEffect(() => {
    if (!similarDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (similarDropdownRef.current && !similarDropdownRef.current.contains(e.target)) {
        setSimilarDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [similarDropdownOpen]);

  useEffect(() => {
    if (!eventTypeMultiOpen) return;
    const handleClickOutside = (e) => {
      if (eventTypeMultiRef.current && !eventTypeMultiRef.current.contains(e.target)) {
        setEventTypeMultiOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [eventTypeMultiOpen]);

  const toggleEventTypeId = (id) => {
    const sid = String(id);
    setForm((p) => {
      const cur = Array.isArray(p.eventTypeIds) ? p.eventTypeIds.map(String) : [];
      const has = cur.includes(sid);
      const next = has ? cur.filter((x) => x !== sid) : [...cur, sid];
      return { ...p, eventTypeIds: next };
    });
  };

  return (
    <div className="module-page">
      <PageHeader
        title="Treks"
        subtitle="Manage trek master data, then configure nested packages/schedule/media."
        actions={
          !showForm && canManageTreks ? (
            <button
              className="module-btn"
              type="button"
              onClick={() => {
                setEditing(null);
                setSelectedTrekId(null);
                setForm(emptyForm);
                setShowForm(true);
              }}
            >
              Add Trek
            </button>
          ) : null
        }
      />

      {!showForm ? (
        <>
          <div className="trek-summary-cards">
            <div className="trek-summary-card">
              <span className="trek-summary-label">Total Treks</span>
              <span className="trek-summary-value">{isLoading ? '...' : totalTreks}</span>
            </div>
            <div className="trek-summary-card">
              <span className="trek-summary-label">Active</span>
              <span className="trek-summary-value">{isLoading ? '...' : activeCount}</span>
            </div>
            <div className="trek-summary-card">
              <span className="trek-summary-label">Draft</span>
              <span className="trek-summary-value">{isLoading ? '...' : draftCount}</span>
            </div>
          </div>

          <FilterBar>
            <input
              type="text"
              className="module-filter-input"
              placeholder="Search treks..."
              value={searchFilter}
              onChange={(e) => setQuery({ search: e.target.value })}
            />
            <select
              id="trek-category-filter"
              className="module-filter-select"
              value={categoryFilter}
              onChange={(e) => setQuery({ categoryId: e.target.value })}
            >
              <option value="">All Campsites</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </FilterBar>

          <div className="module-card trek-inventory-card">
            <div className="trek-inventory-head">
              <div>
                <h2 className="trek-inventory-title">Treks Inventory</h2>
                <p className="module-subtitle">Manage and monitor all trekking and adventure events</p>
              </div>
            </div>
            {isLoading ? (
              <p className="module-loading">Loading treks...</p>
            ) : paginatedTreks.length === 0 ? (
              <EmptyState
                title="No treks found"
                description="Try adjusting search or campsite filter, or add a new trek."
                actionLabel={canManageTreks ? 'Add Trek' : undefined}
                onAction={
                  canManageTreks
                    ? () => {
                        setEditing(null);
                        setSelectedTrekId(null);
                        setForm(emptyForm);
                        setShowForm(true);
                      }
                    : undefined
                }
              />
            ) : (
              <>
                <table className="module-table trek-inventory-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Code</th>
                      <th>Event Name</th>
                      <th>Campsite</th>
                      <th>Region</th>
                      <th>Total Batches</th>
                      <th>Status</th>
                      {canManageTreks && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTreks.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id ?? '—'}</td>
                        <td>{item.code || '-'}</td>
                        <td>{item.name}</td>
                        <td>
                          {item.category ? (
                            <span className="trek-category-pill">{item.category.name}</span>
                          ) : '-'}
                        </td>
                        <td>{item.location || '-'}</td>
                        <td>
                          {Number(item.totalBatches ?? item.batches?.length ?? 0) > 0 ? (
                            <Link
                              to={`/event-batches?trekId=${item.id}`}
                              className="trek-batch-count-link"
                              title="View event batches for this trek"
                            >
                              {`${Number(item.totalBatches ?? item.batches?.length ?? 0)} Batches`}
                            </Link>
                          ) : (
                            `${Number(item.totalBatches ?? item.batches?.length ?? 0)} Batches`
                          )}
                        </td>
                        <td>
                          <div className="trek-inventory-status-cell">
                            {String(item.status || '').toLowerCase() === 'draft' && (
                              <StatusChip status="draft" label="Draft" />
                            )}
                            {canManageTreks ? (
                              <label
                                className="events-toggle trek-inventory-active-toggle"
                                title={
                                  String(item.status || '').toLowerCase() === 'active'
                                    ? 'Active on site (when batches exist). Turn off to hide this trek.'
                                    : 'Turn on to set this trek to active (visible when batches exist).'
                                }
                              >
                                <input
                                  type="checkbox"
                                  aria-label={
                                    String(item.status || '').toLowerCase() === 'active'
                                      ? 'Trek active — click to deactivate'
                                      : 'Trek inactive — click to activate'
                                  }
                                  checked={String(item.status || '').toLowerCase() === 'active'}
                                  disabled={
                                    updateMutation.isLoading &&
                                    String(updateMutation.variables?.id) === String(item.id)
                                  }
                                  onChange={(e) => {
                                    const nextActive = e.target.checked;
                                    updateMutation.mutate({
                                      id: item.id,
                                      payload: {
                                        status: nextActive ? 'active' : 'inactive',
                                        isActive: nextActive
                                      }
                                    });
                                  }}
                                />
                                <span className="events-toggle-slider" aria-hidden />
                              </label>
                            ) : (
                              <span
                                className={`module-badge ${item.status === 'active' ? 'active' : item.status === 'draft' ? 'draft' : 'inactive'}`}
                              >
                                {item.status || (item.isActive ? 'active' : 'inactive')}
                              </span>
                            )}
                          </div>
                        </td>
                        {canManageTreks && (
                          <td className="module-actions">
                            <button className="module-link-btn" onClick={() => onEdit(item)}>Edit</button>
                            <button className="module-link-btn danger" onClick={() => { if (window.confirm('Delete trek?')) deleteMutation.mutate(item.id); }}>Delete</button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {totalTreks > 0 ? (
                  <div className="trek-inventory-pagination">
                    <span>Showing {(inventoryPage - 1) * INVENTORY_PAGE_SIZE + 1} to {Math.min(inventoryPage * INVENTORY_PAGE_SIZE, totalTreks)} of {totalTreks} treks.</span>
                    <div className="trek-pagination-btns">
                      <button
                        type="button"
                        className="module-btn secondary"
                        disabled={inventoryPage <= 1}
                        onClick={() => setQuery({ page: String(inventoryPage - 1) }, { resetPage: false })}
                      >
                        Previous
                      </button>
                      <span className="trek-page-num">Page {inventoryPage} of {totalPages}</span>
                      <button
                        type="button"
                        className="module-btn secondary"
                        disabled={inventoryPage >= totalPages}
                        onClick={() => setQuery({ page: String(inventoryPage + 1) }, { resetPage: false })}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </>
      ) : null}

      {showForm ? (
      <div className="module-card legacy-trek-form-wrap">
        <div className="trek-editor-head">
          <div className="trek-editor-head-main">
            <button
              type="button"
              className="trek-editor-back-btn"
              onClick={closeTrekEditor}
              aria-label="Back to treks list"
            >
              <ArrowLeft size={20} strokeWidth={2} aria-hidden />
              Back
            </button>
            <div className="trek-editor-head-titles">
              <h2 className="legacy-form-title">{editing ? 'Edit Trek Event' : 'Create New Trek Event'}</h2>
              <p className="module-subtitle">{editing ? 'Update details and publish changes.' : 'Draft and publish your trek event.'}</p>
            </div>
          </div>
          <div className="trek-editor-head-actions">
            <button
              className="module-btn secondary"
              type="button"
              onClick={() => submitTrek('draft')}
            >
              Save Progress
            </button>
            <button
              className="module-btn"
              type="button"
              onClick={() => submitTrek('active')}
            >
              Publish Event
            </button>
          </div>
        </div>
        <form onSubmit={onSubmit}>
          {trekEditorLoading ? (
            <p className="module-subtitle" style={{ margin: '0 0 0.75rem' }}>
              Loading full description, FAQs, and policies…
            </p>
          ) : null}
          <div className="trek-editor-grid">
            <div className="trek-editor-main">
              <section className="trek-editor-section">
                <h3 className="trek-editor-section-title">Basic Information</h3>
                <div className="module-form-grid">
                  <div>
                    <label>Campsite</label>
                    <select value={form.categoryId} onChange={(e) => setForm((p) => ({ ...p, categoryId: e.target.value }))}>
                      <option value="">Select Campsite</option>
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="trek-event-type-multi" ref={eventTypeMultiRef}>
                    <label>Event Types</label>
                    <button
                      type="button"
                      className="trek-event-type-multi-trigger"
                      onClick={() => setEventTypeMultiOpen((o) => !o)}
                    >
                      {(() => {
                        const sel = Array.isArray(form.eventTypeIds) ? form.eventTypeIds.map(String) : [];
                        if (!sel.length) return 'Select event types';
                        const labels = sel
                          .map((id) => eventTypes.find((t) => String(t.id) === id)?.name)
                          .filter(Boolean);
                        return labels.length ? labels.join(', ') : `${sel.length} selected`;
                      })()}
                    </button>
                    {eventTypeMultiOpen ? (
                      <div className="trek-event-type-multi-panel">
                        {eventTypes.length === 0 ? (
                          <div className="trek-event-type-multi-empty">No event types defined.</div>
                        ) : (
                          eventTypes.map((t) => (
                            <label key={t.id} className="trek-event-type-multi-row">
                              <input
                                type="checkbox"
                                checked={(form.eventTypeIds || []).map(String).includes(String(t.id))}
                                onChange={() => toggleEventTypeId(t.id)}
                              />
                              <span>{t.name}</span>
                            </label>
                          ))
                        )}
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <label>Location</label>
                    <select value={form.locationId || ''} onChange={(e) => setForm((p) => ({ ...p, locationId: e.target.value }))}>
                      <option value="">Select Location</option>
                      {locations.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <small className="form-hint">Master location list (filters &amp; grouping).</small>
                  </div>
                  <div>
                    <label>Refund calculator category</label>
                    <select
                      value={form.refundCalculatorCategoryId || ''}
                      onChange={(e) => setForm((p) => ({ ...p, refundCalculatorCategoryId: e.target.value }))}
                    >
                      <option value="">Default policy (45 / 30 / 15 days)</option>
                      {refundCategories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label>Advance Payment</label>
                    <label className="module-switch-row" style={{ justifyContent: 'flex-start', gap: '0.75rem', minHeight: 42 }}>
                      <input
                        type="checkbox"
                        checked={form.advancePaymentEnabled !== false}
                        onChange={(e) => setForm((p) => ({ ...p, advancePaymentEnabled: e.target.checked }))}
                      />
                      <span>{form.advancePaymentEnabled !== false ? 'On' : 'Off'}</span>
                    </label>
                    <small className="form-hint">Off means customers must pay the full amount, even when the price is above ₹5,000.</small>
                  </div>
                  <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 8, padding: '0.75rem 0.9rem', display: 'flex', flexDirection: 'column', gap: '0.35rem', justifyContent: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                      <span style={{ fontSize: '1.05rem' }}>💬</span>
                      <strong style={{ fontSize: '0.82rem', color: '#1e293b' }}>WhatsApp Automation</strong>
                    </div>
                    <small style={{ color: '#64748b', fontSize: '0.73rem', lineHeight: 1.35 }}>
                      Lead WhatsApp templates (Get PDF, Request Callback, Manual Leads) are now centrally managed with multi-template &amp; media support.
                    </small>
                    {(editing || selectedTrekId) ? (
                      <a
                        href={`/whatsapp-automation?trekId=${editing || selectedTrekId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.3rem',
                          marginTop: '0.15rem',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#0284c7',
                          textDecoration: 'none'
                        }}
                      >
                        Configure WhatsApp Templates ↗
                      </a>
                    ) : (
                      <small style={{ color: '#94a3b8', fontSize: '0.7rem' }}>
                        Save trek first to configure WhatsApp templates.
                      </small>
                    )}
                  </div>
                  <div style={{ gridColumn: '1 / span 3' }}>
                    <label>Event Name</label>
                    <input value={form.name} onChange={(e) => handleEventNameChange(e.target.value)} />
                  </div>
                  <div>
                    <label>Event Code</label>
                    <input
                      value={form.code}
                      onChange={(e) => {
                        setForm((p) => ({ ...p, code: e.target.value }));
                        if (eventCodeError) setEventCodeError('');
                      }}
                      onBlur={() => {
                        void checkEventCodeUnique(form.code);
                      }}
                      aria-invalid={Boolean(eventCodeError)}
                    />
                    {eventCodeError ? (
                      <small style={{ display: 'block', marginTop: '0.35rem', color: '#b91c1c' }}>
                        {eventCodeError}
                      </small>
                    ) : (
                      <small className="form-hint">Must be unique across all treks.</small>
                    )}
                  </div>
                  <div>
                    <label>Region</label>
                    <input value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} />
                    <small className="form-hint">Geographic region shown on cards (e.g. Himachal Pradesh). Not the same as Location above.</small>
                  </div>
                  <div>
                    <label>Slug</label>
                    <input
                      value={form.slug}
                      onChange={(e) => {
                        slugAutoSourceNameRef.current = '';
                        setForm((p) => ({ ...p, slug: slugifyTrekName(e.target.value) }));
                      }}
                      disabled={!canEditSlugs}
                    />
                    <small className="form-hint">
                      {canEditSlugs
                        ? 'Auto-generated from event name; you can edit if needed.'
                        : 'Auto-generated from event name.'}
                    </small>
                  </div>
                </div>
              </section>

              <section className="trek-editor-section">
                <h3 className="trek-editor-section-title">Detailed Description</h3>
                <div className="module-form-grid">
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label>Tagline</label>
                    <input value={form.tagline} onChange={(e) => setForm((p) => ({ ...p, tagline: e.target.value }))} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label>SEO — Meta title</label>
                    <input
                      value={form.metaTitle}
                      onChange={(e) => setForm((p) => ({ ...p, metaTitle: e.target.value }))}
                      placeholder="e.g. Kedarkantha Trek | Invincible NGO (auto-generated if empty)"
                    />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label>SEO — Meta description</label>
                    <textarea
                      rows={3}
                      value={form.metaDescription}
                      onChange={(e) => setForm((p) => ({ ...p, metaDescription: e.target.value }))}
                      placeholder="Short summary for Google (max ~160 chars; auto-generated from tagline if empty)"
                    />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label>About The Event</label>
                    {blockRichEditorsWhileFetching ? (
                      <p className="module-subtitle" style={{ margin: '0.35rem 0 0.75rem' }}>Loading editor…</p>
                    ) : (
                      <AdminRichTextEditor
                        key={`about-${editing || 'new'}-${richTextMountKey}`}
                        value={form.description}
                        onChange={(value) => setForm((p) => ({ ...p, description: value }))}
                        hint="Shown on the public event page (About The Trek). Use lists, bold, and links as needed."
                        placeholder="Describe the trek experience..."
                      />
                    )}
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label>Important Update</label>
                    {blockRichEditorsWhileFetching ? (
                      <p className="module-subtitle" style={{ margin: '0.35rem 0 0.75rem' }}>Loading editor…</p>
                    ) : (
                      <AdminRichTextEditor
                        key={`important-${editing || 'new'}-${richTextMountKey}`}
                        compact
                        value={form.importantUpdate}
                        onChange={(value) => setForm((p) => ({ ...p, importantUpdate: value }))}
                        hint="Optional notice for the event page (alerts, schedule changes)."
                        placeholder="Optional update for travellers..."
                      />
                    )}
                  </div>
                  <div
                    key={`faqs-${editing || 'new'}-${richTextMountKey}`}
                    style={{ gridColumn: '1 / -1' }}
                    className="trek-faq-editor"
                  >
                    <label>FAQs</label>
                    <p className="trek-faq-hint">Add questions and answers. They will appear on the event detail page.</p>
                    {blockRichEditorsWhileFetching ? (
                      <p className="module-subtitle" style={{ margin: '0.35rem 0 0.75rem' }}>Loading FAQ editors…</p>
                    ) : (
                      (() => {
                        let list = [];
                        try {
                          const parsed = typeof form.faqs === 'string' ? JSON.parse(form.faqs || '[]') : form.faqs;
                          list = Array.isArray(parsed) ? parsed : [];
                        } catch {
                          list = [];
                        }
                        const setFaqList = (newList) => setForm((p) => ({ ...p, faqs: JSON.stringify(newList) }));
                        return (
                          <>
                            {list.map((faq, i) => (
                              <div key={i} className="trek-faq-item">
                                <input
                                  type="text"
                                  value={faq.q || ''}
                                  onChange={(e) => setFaqList(list.map((f, j) => j === i ? { ...f, q: e.target.value } : f))}
                                  placeholder="Question"
                                />
                                <div className="trek-faq-answer-editor">
                                  <AdminRichTextEditor
                                    compact
                                    value={faq.a || ''}
                                    onChange={(value) => setFaqList(list.map((f, j) => (j === i ? { ...f, a: value } : f)))}
                                    placeholder="Answer"
                                  />
                                </div>
                                <button type="button" className="trek-faq-remove" onClick={() => setFaqList(list.filter((_, j) => j !== i))}>Remove</button>
                              </div>
                            ))}
                            <button type="button" className="trek-faq-add" onClick={() => setFaqList([...list, { q: '', a: '' }])}>+ Add FAQ</button>
                          </>
                        );
                      })()
                    )}
                  </div>
                  <div style={{ gridColumn: '1 / -1' }} className="trek-editor-policies-head">
                    <h4 className="trek-editor-subtitle">Quick links &amp; policies</h4>
                    <p className="trek-editor-hint">
                      Add any number of items. Each appears as a button on the event page. Icon list matches package &quot;Includes&quot; (emojis + Lucide). Use the editor for the modal body (headings, lists, links, colors, and tables via the table toolbar). Imported tables stay on save even if the editor preview looks empty — check the preview below.
                    </p>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }} className="trek-quick-policy-list">
                    {(Array.isArray(form.quickPolicyLinks) ? form.quickPolicyLinks : []).map((row, idx) => (
                      <div key={row.id || idx} className="trek-quick-policy-item module-card">
                        <div className="module-form-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', alignItems: 'end' }}>
                          <div>
                            <label>Icon</label>
                            <FullIconSelect
                              value={row.icon || ''}
                              onChange={(icon) =>
                                setForm((p) => {
                                  const list = [...(p.quickPolicyLinks || [])];
                                  list[idx] = { ...list[idx], icon };
                                  return { ...p, quickPolicyLinks: list };
                                })
                              }
                              defaultLabel="Default (briefcase)"
                            />
                          </div>
                          <div style={{ gridColumn: 'span 3' }}>
                            <label>Title</label>
                            <input
                              value={row.title || ''}
                              onChange={(e) =>
                                setForm((p) => {
                                  const list = [...(p.quickPolicyLinks || [])];
                                  list[idx] = { ...list[idx], title: e.target.value };
                                  return { ...p, quickPolicyLinks: list };
                                })
                              }
                              placeholder="e.g. Things to carry"
                            />
                          </div>
                        </div>
                        <label style={{ display: 'block', marginTop: '0.65rem' }}>Content</label>
                        {blockRichEditorsWhileFetching ? (
                          <p className="module-subtitle" style={{ margin: '0.35rem 0 0.75rem' }}>Loading editor…</p>
                        ) : (
                          <>
                            <AdminRichTextEditor
                              ref={(el) => {
                                const key = row.id || `idx-${idx}`;
                                if (el) quickPolicyEditorRefStore[key] = el;
                                else delete quickPolicyEditorRefStore[key];
                              }}
                              key={`qp-${row.id || idx}-${richTextMountKey}`}
                              compact
                              value={
                                String(row.content || '').trim() ||
                                String(quickPolicyContentBackupStore.get(row.id) || '')
                              }
                              onChange={(value) => {
                                const v = normalizeQuillEmpty(value);
                                const backup =
                                  quickPolicyContentBackupStore.get(row.id) ||
                                  String(row.content || '');
                                if (!v && htmlHasRichContent(backup)) return;
                                if (v) quickPolicyContentBackupStore.set(row.id, value);
                                setForm((p) => {
                                  const list = [...(p.quickPolicyLinks || [])];
                                  list[idx] = { ...list[idx], content: value };
                                  return { ...p, quickPolicyLinks: list };
                                });
                              }}
                              placeholder="Policy or list details…"
                            />
                            {(() => {
                              const previewHtml =
                                String(row.content || '').trim() ||
                                String(quickPolicyContentBackupStore.get(row.id) || '');
                              if (!htmlHasRichContent(previewHtml)) return null;
                              const editorShowsContent = htmlHasRichContent(row.content);
                              if (editorShowsContent && !htmlHasTable(previewHtml)) return null;
                              return (
                                <div className="trek-quick-policy-table-preview" aria-label="Saved content preview">
                                  <p className="trek-editor-hint" style={{ margin: '0.5rem 0 0.35rem' }}>
                                    {htmlHasTable(previewHtml)
                                      ? 'Saved table preview (as on the website):'
                                      : 'Saved content preview (as on the website — editor may not show all legacy formatting):'}
                                  </p>
                                  <div
                                    className="rich-content rich-content-html trek-quick-policy-table-preview-inner"
                                    dangerouslySetInnerHTML={{
                                      __html: sanitizeRichHtml(previewHtml)
                                    }}
                                  />
                                </div>
                              );
                            })()}
                          </>
                        )}
                        <button
                          type="button"
                          className="module-btn secondary trek-quick-policy-remove"
                          onClick={() =>
                            setForm((p) => ({
                              ...p,
                              quickPolicyLinks: (p.quickPolicyLinks || []).filter((_, j) => j !== idx)
                            }))
                          }
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="module-btn secondary"
                      onClick={() =>
                        setForm((p) => ({
                          ...p,
                          quickPolicyLinks: [
                            ...(p.quickPolicyLinks || []),
                            { id: newQuickPolicyRowId(), icon: 'briefcase', title: '', content: '' }
                          ]
                        }))
                      }
                    >
                      + Add policy / link
                    </button>
                  </div>
                </div>
              </section>

              <section className="trek-editor-section">
                <h3 className="trek-editor-section-title">Logistics & Difficulty</h3>
                <p className="trek-editor-hint">
                  Shown on the event detail page (below &quot;About The Trek&quot;, above &quot;Join Us From&quot;) as a grid of stats. Duration is taken from batch dates / itinerary; other rows use the fields here. Icons are optional (defaults apply on the site).
                </p>
                <div className="trek-logistics-fields">
                  <div className="trek-logistics-row trek-logistics-row-duration">
                    <FullIconSelect
                      className="trek-logistics-icon-select"
                      value={form.logisticsIcons?.duration || ''}
                      onChange={(icon) =>
                        setForm((p) => ({
                          ...p,
                          logisticsIcons: { ...(p.logisticsIcons || emptyLogisticsIcons()), duration: icon }
                        }))
                      }
                      iconOnly
                    />
                    <div className="trek-logistics-input-wrap trek-logistics-derived">
                      <label>Duration (on event page)</label>
                      <p className="trek-logistics-derived-hint">
                        Computed from departure dates and packages — not editable here. Icon only.
                      </p>
                    </div>
                  </div>
                  <div className="trek-logistics-row">
                    <FullIconSelect
                      className="trek-logistics-icon-select"
                      value={form.logisticsIcons?.lengthKm || ''}
                      onChange={(icon) =>
                        setForm((p) => ({
                          ...p,
                          logisticsIcons: { ...(p.logisticsIcons || emptyLogisticsIcons()), lengthKm: icon }
                        }))
                      }
                      iconOnly
                    />
                    <div className="trek-logistics-input-wrap">
                      <label>Length (in km)</label>
                      <input type="number" value={form.lengthKm} onChange={(e) => setForm((p) => ({ ...p, lengthKm: e.target.value }))} />
                    </div>
                  </div>
                  <div className="trek-logistics-row">
                    <FullIconSelect
                      className="trek-logistics-icon-select"
                      value={form.logisticsIcons?.age || ''}
                      onChange={(icon) =>
                        setForm((p) => ({
                          ...p,
                          logisticsIcons: { ...(p.logisticsIcons || emptyLogisticsIcons()), age: icon }
                        }))
                      }
                      iconOnly
                    />
                    <div className="trek-logistics-input-wrap trek-logistics-age-pair">
                      <div>
                        <label>Min age</label>
                        <input type="number" value={form.minAge} onChange={(e) => setForm((p) => ({ ...p, minAge: e.target.value }))} />
                      </div>
                      <div>
                        <label>Max age</label>
                        <input type="number" value={form.maxAge} onChange={(e) => setForm((p) => ({ ...p, maxAge: e.target.value }))} />
                      </div>
                    </div>
                  </div>
                  <div className="trek-logistics-row">
                    <FullIconSelect
                      className="trek-logistics-icon-select"
                      value={form.logisticsIcons?.difficulty || ''}
                      onChange={(icon) =>
                        setForm((p) => ({
                          ...p,
                          logisticsIcons: { ...(p.logisticsIcons || emptyLogisticsIcons()), difficulty: icon }
                        }))
                      }
                      iconOnly
                    />
                    <div className="trek-logistics-input-wrap">
                      <label>Difficulty level</label>
                      <select value={form.difficulty} onChange={(e) => setForm((p) => ({ ...p, difficulty: e.target.value }))}>
                        <option value="">Select Difficulty</option>
                        {form.difficulty && !ALL_TREK_DIFFICULTY_VALUES.includes(form.difficulty) ? (
                          <option value={form.difficulty}>{form.difficulty} (legacy — pick a new level)</option>
                        ) : null}
                        {TREK_DIFFICULTY_GROUPS.map((group) => (
                          <optgroup key={group.dots} label={group.label}>
                            {group.options.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="trek-logistics-row">
                    <FullIconSelect
                      className="trek-logistics-icon-select"
                      value={form.logisticsIcons?.altitude || ''}
                      onChange={(icon) =>
                        setForm((p) => ({
                          ...p,
                          logisticsIcons: { ...(p.logisticsIcons || emptyLogisticsIcons()), altitude: icon }
                        }))
                      }
                      iconOnly
                    />
                    <div className="trek-logistics-input-wrap">
                      <label>Altitude</label>
                      <input value={form.altitude} onChange={(e) => setForm((p) => ({ ...p, altitude: e.target.value }))} />
                    </div>
                  </div>
                </div>
              </section>

              <section className="trek-editor-section">
                <h3 className="trek-editor-section-title">Badges (chips under title)</h3>
                <p className="trek-editor-hint">
                  These small chips appear below the trek title on the event detail page (e.g. &quot;Group Trip&quot;, &quot;Winter Special&quot;).
                  Icon picker matches logistics and package includes (same Lucide set as the public site).
                </p>
                <div className="trek-stay-options-list">
                  {(form.badges || []).map((badge, idx) => (
                    <div
                      key={idx}
                      className={`trek-stay-option-row trek-badge-row${badgeDragIdx === idx ? ' trek-badge-row--dragging' : ''}`}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', String(idx));
                        setBadgeDragIdx(idx);
                      }}
                      onDragEnd={() => setBadgeDragIdx(null)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const fromIdx = badgeDragIdx;
                        if (fromIdx !== null && fromIdx !== idx) {
                          const updated = [...(form.badges || [])];
                          const [moved] = updated.splice(fromIdx, 1);
                          updated.splice(idx, 0, moved);
                          setForm((p) => ({ ...p, badges: updated }));
                        }
                        setBadgeDragIdx(null);
                      }}
                    >
                      <div className="trek-schedule-grip" style={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: '#94a3b8', padding: '0 0.25rem' }} title="Drag to reorder">
                        <GripVertical size={16} />
                      </div>
                      <FullIconSelect
                        className="trek-badge-icon-select"
                        value={badge.icon || ''}
                        onChange={(icon) =>
                          setForm((p) => ({
                            ...p,
                            badges: (p.badges || []).map((b, i) => (i === idx ? { ...b, icon } : b))
                          }))
                        }
                        iconOnly
                      />
                      <input
                        placeholder="Label (e.g. Group Trip)"
                        value={badge.label || ''}
                        onChange={(e) => setForm((p) => ({
                          ...p,
                          badges: (p.badges || []).map((b, i) => (i === idx ? { ...b, label: e.target.value } : b))
                        }))}
                        className="trek-stay-option-name"
                        style={{ height: '38px', boxSizing: 'border-box' }}
                      />
                      <button
                        type="button"
                        className="trek-badge-remove-btn"
                        onClick={() => setForm((p) => ({ ...p, badges: (p.badges || []).filter((_, i) => i !== idx) }))}
                        title="Remove badge"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="module-btn secondary"
                    onClick={() => setForm((p) => ({ ...p, badges: [...(p.badges || []), { label: '', icon: '' }] }))}
                  >
                    Add badge
                  </button>
                </div>
              </section>

              <section className="trek-editor-section">
                <h3 className="trek-editor-section-title">Similar Adventures</h3>
                <p className="trek-editor-hint">
                  Only treks that have at least one upcoming event batch (start after today) are listed. Choose which appear in the &quot;Similar Adventures&quot; section on this event&apos;s detail page. Order is preserved.
                </p>
                <div className="trek-similar-dropdown-wrap" ref={similarDropdownRef}>
                  {(() => {
                    const options = similarAdventureOptions;
                    const selectedIds = form.similarTrekIds || [];
                    const selectedCount = selectedIds.length;
                    const label = selectedCount > 0
                      ? `${selectedCount} trek${selectedCount !== 1 ? 's' : ''} selected`
                      : 'Select similar adventures…';
                    if (upcomingForSimilarLoading) {
                      return <p className="trek-editor-hint">Loading treks with upcoming batches…</p>;
                    }
                    if (!options.length) {
                      return (
                        <p className="trek-editor-hint">
                          No other treks with upcoming event batches. Add batches under Event Batches, then return here to link similar adventures.
                        </p>
                      );
                    }
                    return (
                      <>
                        <button
                          type="button"
                          className="trek-similar-dropdown-btn"
                          onClick={() => setSimilarDropdownOpen((o) => !o)}
                          aria-expanded={similarDropdownOpen}
                          aria-haspopup="listbox"
                        >
                          {label}
                          <span className="trek-similar-dropdown-arrow" aria-hidden>{similarDropdownOpen ? ' ▲' : ' ▼'}</span>
                        </button>
                        {similarDropdownOpen && (
                          <div className="trek-similar-dropdown-panel" role="listbox">
                            {options.map((t) => {
                              const idNum = Number(t.id);
                              const checked = selectedIds.includes(idNum) || selectedIds.includes(t.id);
                              return (
                                <label key={t.id} className="trek-similar-check">
                                  <input
                                    type="checkbox"
                                    checked={!!checked}
                                    onChange={() => {
                                      setForm((p) => {
                                        const ids = p.similarTrekIds || [];
                                        const has = ids.includes(idNum) || ids.includes(t.id);
                                        const next = has ? ids.filter((x) => x !== idNum && x !== t.id) : [...ids, idNum];
                                        return { ...p, similarTrekIds: next };
                                      });
                                    }}
                                  />
                                  <span>{t.name}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </section>

              <section className="trek-editor-section">
                <h3 className="trek-editor-section-title">Stay Options</h3>
                <p className="trek-editor-hint">Options shown on the event detail page (e.g. Quad Sharing, Triple Sharing). Price Adjust is added to the base price when selected. Leave empty to hide the section.</p>
                <div className="trek-stay-options-list">
                  {(form.stayOptions || []).map((opt, idx) => (
                    <div key={`stay-opt-${idx}`} className="trek-stay-option-row">
                      <input
                        placeholder="Id (e.g. quad)"
                        value={opt.id || ''}
                        onChange={(e) => setForm((p) => ({
                          ...p,
                          stayOptions: p.stayOptions.map((o, i) => (i === idx ? { ...o, id: e.target.value } : o))
                        }))}
                        className="trek-stay-option-id"
                      />
                      <input
                        placeholder="Name (e.g. Quad Sharing)"
                        value={opt.name || ''}
                        onChange={(e) => setForm((p) => ({
                          ...p,
                          stayOptions: p.stayOptions.map((o, i) => (i === idx ? { ...o, name: e.target.value } : o))
                        }))}
                        className="trek-stay-option-name"
                      />
                      <input
                        type="number"
                        placeholder="Price adjust"
                        value={
                          opt.priceAdjust != null && opt.priceAdjust !== ''
                            ? priceInputString(opt.priceAdjust)
                            : ''
                        }
                        onChange={(e) => setForm((p) => ({
                          ...p,
                          stayOptions: p.stayOptions.map((o, i) => (i === idx ? { ...o, priceAdjust: e.target.value === '' ? 0 : Number(e.target.value) } : o))
                        }))}
                        className="trek-stay-option-adjust"
                      />
                      <button
                        type="button"
                        className="module-btn secondary trek-stay-option-remove"
                        onClick={() => setForm((p) => ({ ...p, stayOptions: p.stayOptions.filter((_, i) => i !== idx) }))}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="module-btn secondary"
                    onClick={() => setForm((p) => ({ ...p, stayOptions: [...(p.stayOptions || []), { id: '', name: '', priceAdjust: 0 }] }))}
                  >
                    Add stay option
                  </button>
                </div>
              </section>

              <section className="trek-editor-section">
                <h3 className="trek-editor-section-title">Stay Details</h3>
                <p className="trek-editor-hint">Accommodation cards shown on the event detail page (image, duration, rating, features). Leave empty to hide the section.</p>
                <div className="trek-stay-details-list">
                  {(form.stayDetails || []).map((stay, sIdx) => (
                    <div key={sIdx} className="trek-stay-detail-card">

                      {/* Card header: name + remove button */}
                      <div className="trek-stay-detail-header">
                        <div className="trek-stay-detail-name-wrap">
                          {/* Show SVG icon for selected stayType */}
                          <span className="trek-stay-type-header-icon" title={stay.stayType || 'Accommodation'}>
                            {stay.stayType === 'hotel' && (
                              <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="27" width="28" height="3" rx="0.5"/><rect x="2" y="14" width="8" height="13"/>
                                <rect x="22" y="14" width="8" height="13"/><rect x="8" y="3" width="16" height="24"/>
                                <rect x="3.5" y="16" width="3" height="3" rx="0.5"/><rect x="3.5" y="21" width="3" height="3" rx="0.5"/>
                                <rect x="25.5" y="16" width="3" height="3" rx="0.5"/><rect x="25.5" y="21" width="3" height="3" rx="0.5"/>
                                <rect x="10" y="5.5" width="3" height="3" rx="0.5"/><rect x="14.5" y="5.5" width="3" height="3" rx="0.5"/><rect x="19" y="5.5" width="3" height="3" rx="0.5"/>
                                <rect x="10" y="11" width="3" height="3" rx="0.5"/><rect x="14.5" y="11" width="3" height="3" rx="0.5"/><rect x="19" y="11" width="3" height="3" rx="0.5"/>
                                <rect x="13" y="18.5" width="6" height="8.5"/>
                              </svg>
                            )}
                            {stay.stayType === 'campsite' && (
                              <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M30 7a5 5 0 1 0-5 5 3.5 3.5 0 0 1 5-5z"/>
                                <line x1="5" y1="20" x2="5" y2="28"/><line x1="3" y1="28" x2="7" y2="28"/>
                                <path d="M5 20 C2 20 0 17 1 14 C0 11 2 8 4 9 C4 7 6 6 7 8 C9 7 11 10 9 13 C11 14 10 18 8 19 C7 20 5 20 5 20Z"/>
                                <line x1="5" y1="15" x2="2" y2="18"/><line x1="5" y1="18" x2="2" y2="21"/>
                                <line x1="27" y1="20" x2="27" y2="28"/><line x1="25" y1="28" x2="29" y2="28"/>
                                <path d="M27 20 C30 20 32 17 31 14 C32 11 30 8 28 9 C28 7 26 6 25 8 C23 7 21 10 23 13 C21 14 22 18 24 19 C25 20 27 20 27 20Z"/>
                                <line x1="27" y1="15" x2="30" y2="18"/><line x1="27" y1="18" x2="30" y2="21"/>
                                <polyline points="11,28 16,14 21,28"/>
                                <line x1="16" y1="14" x2="16" y2="20"/>
                                <polyline points="13.5,28 16,22 18.5,28"/>
                              </svg>
                            )}
                            {stay.stayType === 'dorm' && (
                              <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M 6 4 L 6 28" />
                                <path d="M 6 13 Q 12 8 14 13" />
                                <path d="M 6 23 Q 12 18 14 23" />
                                <path d="M 6 13 L 26 13 A 2 2 0 0 1 28 15 L 28 28" />
                                <path d="M 6 23 L 28 23" />
                                <path d="M 21 13 L 21 23" />
                                <path d="M 21 18 L 28 18" />
                              </svg>
                            )}
                            {stay.stayType === 'tents' && (
                              <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="2" x2="18" y2="9"/><line x1="20" y1="2" x2="14" y2="9"/>
                                <line x1="16" y1="8" x2="3" y2="29"/><line x1="16" y1="8" x2="29" y2="29"/>
                                <line x1="3" y1="29" x2="29" y2="29"/>
                                <polyline points="12,29 16,21 20,29"/>
                                <line x1="3" y1="28.5" x2="1.5" y2="31"/><line x1="29" y1="28.5" x2="30.5" y2="31"/>
                              </svg>
                            )}
                            {stay.stayType === 'houseboats' && (
                              <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 27 Q3 25 5 27 Q7 29 9 27 Q11 25 13 27 Q15 29 17 27 Q19 25 21 27 Q23 29 25 27 Q27 25 29 27 Q31 29 32 27"/>
                                <rect x="3" y="20" width="26" height="6" rx="1"/>
                                <rect x="6" y="12" width="20" height="9"/><rect x="5" y="9" width="22" height="4"/>
                                <line x1="9" y1="9" x2="9" y2="7"/><line x1="13" y1="9" x2="13" y2="7"/>
                                <line x1="17" y1="9" x2="17" y2="7"/><line x1="21" y1="9" x2="21" y2="7"/><line x1="25" y1="9" x2="25" y2="7"/>
                                <rect x="8" y="14" width="4" height="4" rx="0.5"/><rect x="14" y="14" width="4" height="4" rx="0.5"/><rect x="20" y="14" width="4" height="4" rx="0.5"/>
                                <rect x="25" y="11" width="3" height="5"/>
                                <path d="M3 20 C1 18 0 15 2 13"/>
                              </svg>
                            )}
                            {stay.stayType === 'homestay' && (
                              <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="0" y1="14" x2="3" y2="14"/><line x1="21" y1="14" x2="23" y2="14"/>
                                <line x1="1" y1="14" x2="13" y2="3"/><line x1="13" y1="3" x2="23" y2="12"/>
                                <rect x="3" y="14" width="18" height="14"/>
                                <path d="M10 28 L10 21 Q10 18 13 18 Q16 18 16 21 L16 28"/>
                                <polyline points="23,19 27,14 32,19"/>
                                <rect x="23" y="19" width="9" height="9"/>
                                <rect x="25" y="22" width="5" height="3" rx="0.5"/>
                              </svg>
                            )}
                            {!stay.stayType && (
                              <svg viewBox="0 0 32 32" fill="none" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="27" width="28" height="3" rx="0.5"/><rect x="8" y="3" width="16" height="24"/>
                                <rect x="2" y="14" width="8" height="13"/><rect x="22" y="14" width="8" height="13"/>
                              </svg>
                            )}
                          </span>
                          <input
                            placeholder="Stay name (e.g. Haridwar Hotel)"
                            value={stay.name || ''}
                            onChange={(e) => setForm((p) => ({
                              ...p,
                              stayDetails: (p.stayDetails || []).map((s, i) => (i === sIdx ? { ...s, name: e.target.value } : s))
                            }))}
                            className="trek-stay-detail-name"
                          />
                        </div>
                        <button
                          type="button"
                          title="Remove stay"
                          className="trek-stay-remove-icon-btn"
                          onClick={() => setForm((p) => ({ ...p, stayDetails: (p.stayDetails || []).filter((_, i) => i !== sIdx) }))}
                        >
                          ×
                        </button>
                      </div>

                      {/* Stay Type selector */}
                      <div className="trek-stay-type-row">
                        <label className="trek-stay-field-label"><span>🏠</span> Stay Type</label>
                        <div className="trek-stay-type-select-wrap">
                          <select
                            value={stay.stayType || ''}
                            onChange={(e) => setForm((p) => ({
                              ...p,
                              stayDetails: (p.stayDetails || []).map((s, i) => (i === sIdx ? { ...s, stayType: e.target.value } : s))
                            }))}
                            className="trek-stay-type-select"
                          >
                            <option value="">— Select stay type —</option>
                            <option value="hotel">🏨 Hotel</option>
                            <option value="campsite">⛺ Campsite</option>
                            <option value="dorm">🛏 Dorm</option>
                            <option value="tents">🏕 Tents</option>
                            <option value="houseboats">🚢 Houseboats</option>
                            <option value="homestay">🏡 Homestay</option>
                          </select>
                        </div>
                      </div>

                      {/* Preview photo — upload only */}
                      <div className="trek-stay-detail-photo-row">
                        {stay.imageUrl ? (
                          <div className="trek-stay-preview-thumb-wrap">
                            <img src={stay.imageUrl} alt="preview" className="trek-stay-preview-thumb" />
                            <button
                              type="button"
                              className="trek-stay-preview-remove"
                              title="Remove photo"
                              onClick={() => setForm((p) => ({
                                ...p,
                                stayDetails: (p.stayDetails || []).map((s, i) => (i === sIdx ? { ...s, imageUrl: '' } : s))
                              }))}
                            >✕</button>
                          </div>
                        ) : null}
                        <label className="trek-stay-upload-label">
                          <span className="trek-stay-upload-icon">📷</span>
                          <span>{stay.imageUrl ? 'Replace Photo' : 'Upload Preview Photo'}</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="visually-hidden"
                            onChange={async (e) => {
                              const file = e.target?.files?.[0];
                              if (!file) return;
                              try {
                                const res = await trekAPI.uploadImage(file);
                                const url = res?.data?.data?.url || res?.data?.url || res?.url;
                                if (url) {
                                  setForm((p) => ({
                                    ...p,
                                    stayDetails: (p.stayDetails || []).map((s, i) => (i === sIdx ? { ...s, imageUrl: url } : s))
                                  }));
                                  showToast({ message: 'Photo uploaded', severity: 'success' });
                                } else {
                                  showToast({ message: 'Upload failed: no URL returned', severity: 'error' });
                                }
                              } catch (err) {
                                showToast({ message: err?.response?.data?.message || 'Upload failed', severity: 'error' });
                              }
                              e.target.value = '';
                            }}
                          />
                        </label>
                      </div>

                      {/* Row 1: Duration + Rating */}
                      <div className="trek-stay-detail-grid2">
                        <div className="trek-stay-field-group">
                          <label className="trek-stay-field-label"><span>🌙</span> Duration</label>
                          <input
                            placeholder="e.g. 2 Nights"
                            value={stay.durationLabel || ''}
                            onChange={(e) => setForm((p) => ({
                              ...p,
                              stayDetails: (p.stayDetails || []).map((s, i) => (i === sIdx ? { ...s, durationLabel: e.target.value } : s))
                            }))}
                          />
                        </div>
                        <div className="trek-stay-field-group">
                          <label className="trek-stay-field-label"><span>⭐</span> Rating</label>
                          <input
                            placeholder="e.g. 4.5 / 5"
                            value={stay.ratingLabel || ''}
                            onChange={(e) => setForm((p) => ({
                              ...p,
                              stayDetails: (p.stayDetails || []).map((s, i) => (i === sIdx ? { ...s, ratingLabel: e.target.value } : s))
                            }))}
                          />
                        </div>
                      </div>

                      {/* Gallery — multi-photo upload */}
                      <div className="trek-stay-gallery-section">
                        <span className="trek-stay-field-label"><span>🖼</span> Gallery Photos</span>
                        <div className="trek-stay-gallery-thumbs">
                          {(stay.galleryImages || []).map((gUrl, gIdx) => (
                            <div key={gIdx} className="trek-stay-gallery-thumb-wrap">
                              <img src={gUrl} alt={`gallery-${gIdx}`} className="trek-stay-gallery-thumb" />
                              <button
                                type="button"
                                className="trek-stay-preview-remove"
                                title="Remove"
                                onClick={() => setForm((p) => ({
                                  ...p,
                                  stayDetails: (p.stayDetails || []).map((s, i) =>
                                    i === sIdx ? { ...s, galleryImages: (s.galleryImages || []).filter((_, j) => j !== gIdx) } : s
                                  )
                                }))}
                              >✕</button>
                            </div>
                          ))}
                          <label className="trek-stay-gallery-add-btn">
                            <span>＋ Add Photos</span>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              className="visually-hidden"
                              onChange={async (e) => {
                                const files = Array.from(e.target?.files || []);
                                if (!files.length) return;
                                const uploaded = [];
                                for (const file of files) {
                                  try {
                                    const res = await trekAPI.uploadImage(file);
                                    const url = res?.data?.data?.url || res?.data?.url || res?.url;
                                    if (url) uploaded.push(url);
                                  } catch {}
                                }
                                if (uploaded.length) {
                                  setForm((p) => ({
                                    ...p,
                                    stayDetails: (p.stayDetails || []).map((s, i) =>
                                      i === sIdx ? { ...s, galleryImages: [...(s.galleryImages || []), ...uploaded] } : s
                                    )
                                  }));
                                  showToast({ message: `${uploaded.length} photo(s) uploaded`, severity: 'success' });
                                }
                                e.target.value = '';
                              }}
                            />
                          </label>
                        </div>
                      </div>

                      {/* Features */}
                      <div className="trek-stay-detail-features">
                        <span className="trek-stay-field-label"><span>✅</span> Features</span>
                        {(stay.features || []).map((f, fIdx) => (
                          <div key={fIdx} className="trek-feature-row">
                            <FullIconSelect
                              className="trek-feature-icon"
                              value={f.icon || ''}
                              onChange={(icon) => setForm((p) => ({
                                ...p,
                                stayDetails: (p.stayDetails || []).map((s, i) =>
                                  i === sIdx
                                    ? { ...s, features: (s.features || []).map((ff, j) => (j === fIdx ? { ...ff, icon } : ff)) }
                                    : s
                                )
                              }))}
                            />
                            <input
                              placeholder="Feature text"
                              value={f.text || ''}
                              onChange={(e) => setForm((p) => ({
                                ...p,
                                stayDetails: (p.stayDetails || []).map((s, i) =>
                                  i === sIdx
                                    ? { ...s, features: (s.features || []).map((ff, j) => (j === fIdx ? { ...ff, text: e.target.value } : ff)) }
                                    : s
                                )
                              }))}
                              className="trek-feature-text"
                            />
                            <button
                              type="button"
                              title="Remove feature"
                              className="trek-feature-remove-icon"
                              onClick={() => setForm((p) => ({
                                ...p,
                                stayDetails: (p.stayDetails || []).map((s, i) =>
                                  i === sIdx ? { ...s, features: (s.features || []).filter((_, j) => j !== fIdx) } : s
                                )
                              }))}
                            >✕</button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="module-btn secondary trek-stay-add-feature-btn"
                          onClick={() => setForm((p) => ({
                            ...p,
                            stayDetails: (p.stayDetails || []).map((s, i) =>
                              i === sIdx ? { ...s, features: [...(s.features || []), { icon: '', text: '' }] } : s
                            )
                          }))}
                        >
                          + Add feature
                        </button>
                      </div>

                    </div>
                  ))}
                  <button
                    type="button"
                    className="module-btn secondary"
                    onClick={() => setForm((p) => ({
                      ...p,
                      stayDetails: [...(p.stayDetails || []), { name: '', stayType: '', imageUrl: '', durationLabel: '', ratingLabel: '', galleryImages: [], features: [] }]
                    }))}
                  >
                    + Add stay
                  </button>
                </div>
              </section>

              <section className="trek-editor-section">
                <h3 className="trek-editor-section-title">Pricing & Policies</h3>
                <div className="module-form-grid">
                  <div>
                    <label>Brochure PDF</label>
                    <input
                      type="text"
                      value={form.brochureUrl}
                      onChange={(e) => setForm((p) => ({ ...p, brochureUrl: e.target.value }))}
                      placeholder="Brochure URL"
                    />
                    <div style={{ marginTop: '0.4rem' }}>
                      <input
                        type="file"
                        accept="application/pdf"
                        id="trek-brochure-upload"
                        style={{ display: 'none' }}
                        onChange={handleUploadBrochure}
                      />
                      <button
                        type="button"
                        className="module-btn secondary"
                        onClick={() => document.getElementById('trek-brochure-upload').click()}
                        disabled={isBrochureUploading}
                      >
                        {isBrochureUploading ? 'Uploading...' : 'Upload PDF'}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label>Tips URL</label>
                    <input
                      type="text"
                      value={form.tipsUrl}
                      onChange={(e) => setForm((p) => ({ ...p, tipsUrl: e.target.value }))}
                      placeholder="Tips URL"
                    />
                    <div style={{ marginTop: '0.4rem' }}>
                      <input
                        type="file"
                        accept="image/*,.pdf,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        id="trek-tips-upload"
                        style={{ display: 'none' }}
                        onChange={handleUploadTips}
                      />
                      <button
                        type="button"
                        className="module-btn secondary"
                        onClick={() => document.getElementById('trek-tips-upload').click()}
                        disabled={isTipsUploading}
                      >
                        {isTipsUploading ? 'Uploading...' : 'Upload file'}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label>Status</label>
                    <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
                      <option value="active">active</option>
                      <option value="inactive">inactive</option>
                      <option value="draft">draft</option>
                    </select>
                  </div>
                </div>
              </section>
            </div>

            {/* Website Card Preview removed (requested). */}
          </div>

          <div className="legacy-main-actions">
            <button
              className="module-btn secondary"
              type="button"
              onClick={() => {
                setEditing(null);
                setForm(emptyForm);
                setSelectedTrekId(null);
                setShowForm(false);
              }}
            >
              Cancel
            </button>
          </div>

          <div className="legacy-section-list" style={{ marginTop: '1rem' }}>
            <div className="legacy-section">
              <div className="legacy-section-head">
                <h3>Images</h3>
                <button
                  className="module-btn legacy-add-btn"
                  type="button"
                  disabled={isImageUploading || isReorderingMedia}
                  onClick={() => {
                    imageUploadInputRef.current?.click();
                  }}
                >
                  {isImageUploading ? 'Uploading...' : 'Upload'}
                </button>
                <input
                  ref={imageUploadInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={onUploadImages}
                />
              </div>
              <div className="trek-images-wrap">
                {selectedTrekId ? (
                  <p className="trek-images-reorder-hint">First image is the cover. Drag tiles to reorder.</p>
                ) : null}
                {(sortedMedia || []).length ? (
                  <div className="trek-images-grid">
                    {sortedMedia.map((item) => (
                      <div
                        key={item.id}
                        className={`trek-image-tile${mediaDragId === item.id ? ' trek-image-tile--dragging' : ''}`}
                        draggable={!isImageUploading && !isReorderingMedia}
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData('text/plain', String(item.id));
                          setMediaDragId(item.id);
                        }}
                        onDragEnd={() => setMediaDragId(null)}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const fromId = e.dataTransfer.getData('text/plain') || mediaDragId;
                          if (fromId) onReorderTrekMedia(fromId, item.id);
                        }}
                      >
                        <img src={item.url} alt={item.title || 'Trek image'} draggable={false} />
                        <button
                          type="button"
                          className="trek-image-remove-btn"
                          onClick={() => onDeleteImage(item.id)}
                          aria-label="Remove image"
                          title="Remove image"
                        >
                          x
                        </button>
                      </div>
                    ))}
                  </div>
                ) : selectedTrekId ? (
                  <p className="trek-images-empty">No images uploaded yet.</p>
                ) : null}
              </div>
            </div>
            <NestedTable
              title="Videos"
              rows={videos}
              parentId={selectedTrekId}
              infoMessage={!selectedTrekId ? 'Save trek first, then manage Videos.' : ''}
              promptAdd
              promptMessage="Enter Video YouTube URL"
              mapPromptValue={(value) => {
                const videoId = extractYouTubeVideoId(value);
                if (!videoId) {
                  throw new Error('Invalid YouTube URL');
                }
                return { videoId };
              }}
              allowEdit={false}
              defaults={{ videoId: '' }}
              fields={[{ key: 'videoId', label: 'Video', required: true }]}
              onCreate={(data) => trekAPI.createVideo(selectedTrekId, data).then(invalidateNested)}
              onUpdate={(id, data) => trekAPI.updateVideo(selectedTrekId, id, data).then(invalidateNested)}
              onDelete={(id) => trekAPI.deleteVideo(selectedTrekId, id).then(invalidateNested)}
            />
            <div className="legacy-section">
              <div className="legacy-section-head">
                <h3>Experience Gallery</h3>
              </div>
              {!selectedTrekId ? (
                <p className="trek-images-empty">Save trek first, then add Experience Gallery items.</p>
              ) : (
                <>
                  <p className="trek-exp-gallery-desc">
                    Separate from hero images/videos. Shown in the Experience Gallery on the event page. Add photos (upload or URL) or YouTube videos.
                    <strong> Poster</strong> only for rows with <strong>type video</strong> and a valid YouTube id, and no image URL. Rows with <strong>type image</strong> never show Poster (even if legacy data has a stray video id).
                  </p>
                  <input
                    ref={expGalleryVideoThumbInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={onExperienceGalleryVideoThumbnailFile}
                  />
                  <div className="trek-exp-gallery-actions">
                    <div className="trek-exp-gallery-add-photo">
                      <input
                        ref={expGalleryPhotoInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={() => onAddExperienceGalleryPhoto('file')}
                      />
                      <button
                        type="button"
                        className="module-btn legacy-add-btn"
                        disabled={isExpGalleryAdding}
                        onClick={() => expGalleryPhotoInputRef.current?.click()}
                      >
                        {isExpGalleryAdding ? 'Adding...' : 'Upload photo'}
                      </button>
                      <span className="trek-exp-gallery-or">or</span>
                      <input
                        type="text"
                        className="trek-exp-gallery-url-input"
                        placeholder="Paste image URL"
                        value={expGalleryPhotoUrl}
                        onChange={(e) => setExpGalleryPhotoUrl(e.target.value)}
                      />
                      <button type="button" className="module-btn" disabled={isExpGalleryAdding} onClick={() => onAddExperienceGalleryPhoto('url')}>
                        Add photo
                      </button>
                    </div>
                    <div className="trek-exp-gallery-add-video">
                      <input
                        type="text"
                        className="trek-exp-gallery-url-input"
                        placeholder="YouTube video URL"
                        value={expGalleryVideoUrl}
                        onChange={(e) => setExpGalleryVideoUrl(e.target.value)}
                      />
                      <button type="button" className="module-btn" disabled={isExpGalleryAdding} onClick={onAddExperienceGalleryVideo}>
                        Add video
                      </button>
                    </div>
                  </div>
                  <div className="trek-images-wrap">
                    {(experienceGallery || []).length > 0 ? (
                      <div className="trek-images-grid">
                        {experienceGallery.map((item) => {
                          const imageUrl = expGalleryItemImageUrl(item);
                          const videoId = expGalleryItemVideoId(item);
                          const isVideo = expGalleryItemIsVideoTile(item);
                          const thumb = String(item.thumbnailUrl || item.thumbnail_url || '').trim();
                          const posterSrc = isVideo
                            ? (thumb || `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`)
                            : (imageUrl || '');
                          return (
                            <div key={item.id} className="trek-exp-gallery-item-wrap">
                              <div className="trek-image-tile">
                                {isVideo ? (
                                  <img src={posterSrc} alt={item.title || 'Video'} />
                                ) : (
                                  <img src={posterSrc} alt={item.title || 'Gallery'} onError={(e) => { e.target.style.background = '#eee'; e.target.alt = 'Broken'; }} />
                                )}
                                <button
                                  type="button"
                                  className="trek-image-remove-btn"
                                  onClick={() => onDeleteExperienceGalleryItem(item.id)}
                                  aria-label="Remove"
                                  title="Remove"
                                >
                                  ×
                                </button>
                              </div>
                              {isVideo && (
                                <div className="trek-exp-gallery-video-actions">
                                  <button
                                    type="button"
                                    className="trek-exp-gallery-link-btn"
                                    disabled={isExpGalleryThumbUploading}
                                    onClick={() => onPickExperienceGalleryVideoThumbnail(item.id)}
                                  >
                                    Poster
                                  </button>
                                  {thumb ? (
                                    <button
                                      type="button"
                                      className="trek-exp-gallery-link-btn"
                                      onClick={() => onClearExperienceGalleryVideoThumbnail(item.id)}
                                    >
                                      Default
                                    </button>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="trek-images-empty">No Experience Gallery items yet. Add photos or videos above.</p>
                    )}
                  </div>
                </>
              )}
            </div>
            
            <WebstoriesManager trekId={selectedTrekId} onError={(err) => showToast({ message: err?.response?.data?.message || err.message, severity: 'error' })} />
            
            <div className="legacy-section">
              <div className="legacy-section-head">
                <h3>Packages</h3>
                <button
                  className="module-btn legacy-add-btn"
                  type="button"
                  onClick={handleAddPackageClick}
                >
                  Add
                </button>
              </div>
              <table className="module-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Price</th>
                    <th>Vacancy</th>
                    <th>Priority</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(packages || []).map((pkg) => (
                    <tr key={pkg.id}>
                      <td>{pkg.name}</td>
                      <td>{pkg.description}</td>
                      <td>{pkg.price != null ? formatAdminPrice(pkg.price) : '-'}</td>
                      <td>{pkg.vacancy != null ? pkg.vacancy : '-'}</td>
                      <td>{pkg.priority != null ? pkg.priority : '-'}</td>
                      <td className="module-actions">
                        {pkg.isMain ? (
                          <span className="trek-package-main-badge">Main</span>
                        ) : (
                          <button
                            type="button"
                            className="module-btn trek-package-make-main"
                            onClick={async () => {
                              try {
                                await trekAPI.setPackageMain(selectedTrekId, pkg.id);
                                queryClient.invalidateQueries(['trek-packages', selectedTrekId]);
                              } catch (e) {
                                showToast({
                                  message: e?.response?.data?.message || 'Failed to set main package.',
                                  severity: 'error'
                                });
                              }
                            }}
                          >
                            Make Main
                          </button>
                        )}
                        <Link
                          to={`/treks/${selectedTrekId}/packages/${pkg.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="module-link-btn"
                          style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                          onClick={() => {
                            if (selectedTrekId) persistDraftForNestedNavigation(selectedTrekId);
                          }}
                        >
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="module-link-btn danger"
                          onClick={() => {
                            if (window.confirm('Delete package?')) handleDeletePackage(pkg.id);
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!packages.length ? (
                    <tr>
                      <td colSpan={6}>No packages added yet.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {showPackageForm ? null : null}
            <div className="legacy-section">
              <div className="legacy-section-head">
                <h3>Schedule Days Details</h3>
                <button
                  className="module-btn legacy-add-btn"
                  type="button"
                  onClick={async () => {
                    try {
                      const trekId = await ensureTrekId();
                      persistDraftForNestedNavigation(trekId);
                      navigate(`/treks/${trekId}/schedule/new`);
                    } catch {
                      // ensureTrekId already surfaced validation error
                    }
                  }}
                >
                  Add
                </button>
              </div>
              <table className="module-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Count as Day</th>
                    <th>Order</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(scheduleDays || []).map((day) => (
                    <tr key={day.id}>
                      <td>{day.name}</td>
                      <td>{day.countAsDay}</td>
                      <td>{day.order}</td>
                      <td className="module-actions">
                        <button
                          type="button"
                          className="module-link-btn"
                          onClick={async () => {
                            try {
                              const trekId = await ensureTrekId();
                              persistDraftForNestedNavigation(trekId);
                              navigate(`/treks/${trekId}/schedule/${day.id}`);
                            } catch {
                              // ignore
                            }
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="module-link-btn danger"
                          onClick={async () => {
                            if (!window.confirm('Delete schedule day?')) return;
                            try {
                              const trekId = await ensureTrekId();
                              await trekAPI.deleteScheduleDay(trekId, day.id);
                              invalidateNested();
                            } catch (error) {
                              if (error?.message === 'Validation failed') return;
                              showToast({
                                message: error?.response?.data?.message || 'Failed to delete schedule day.',
                                severity: 'error'
                              });
                            }
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!scheduleDays.length ? (
                    <tr>
                      <td colSpan={4}>No schedule days added yet.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <div className="legacy-section">
              <div className="legacy-section-head">
                <h3>Attractions</h3>
                <button
                  className="module-btn legacy-add-btn"
                  type="button"
                  onClick={handleAddAttractionClick}
                >
                  Add
                </button>
              </div>
              <table className="module-table">
                <thead>
                  <tr>
                    <th>Attraction</th>
                    <th>Order</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(attractions || []).map((attr) => (
                    <tr key={attr.id}>
                      <td>{attr.name}</td>
                      <td>{attr.order}</td>
                      <td className="module-actions">
                        <Link
                          to={`/treks/${selectedTrekId}/attractions/${attr.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="module-link-btn"
                          style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                          onClick={() => {
                            if (selectedTrekId) persistDraftForNestedNavigation(selectedTrekId);
                          }}
                        >
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="module-link-btn danger"
                          onClick={() => {
                            if (!window.confirm('Delete attraction?')) return;
                            handleDeleteAttraction(attr.id);
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!attractions.length ? (
                    <tr>
                      <td colSpan={3}>No attractions added yet.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <div className="legacy-section">
              <div className="legacy-section-head">
                <h3>Inclusions (fee / booking card)</h3>
                <button
                  className="module-btn legacy-add-btn"
                  type="button"
                  onClick={handleAddFeeIncludeClick}
                >
                  Add
                </button>
              </div>
              <p className="legacy-disabled-note" style={{ marginBottom: '0.75rem' }}>
                Shown in the event sidebar &ldquo;Includes&rdquo; grid and in the Inclusion section. Add one row per line; set order and icon when editing.
              </p>
              <table className="module-table">
                <thead>
                  <tr>
                    <th>Icon key</th>
                    <th>Name</th>
                    <th>Order</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(feesIncludes || []).map((fee) => (
                    <tr key={fee.id}>
                      <td>{fee.icon}</td>
                      <td>{fee.name}</td>
                      <td>{fee.order}</td>
                      <td className="module-actions">
                        <Link
                          to={`/treks/${selectedTrekId}/fees-includes/${fee.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="module-link-btn"
                          style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                          onClick={() => {
                            if (selectedTrekId) persistDraftForNestedNavigation(selectedTrekId);
                          }}
                        >
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="module-link-btn danger"
                          onClick={() => {
                            if (!window.confirm('Delete this inclusion?')) return;
                            handleDeleteFeeInclude(fee.id);
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!feesIncludes.length ? (
                    <tr>
                      <td colSpan={4}>
                        No inclusions yet — the site uses default lines until you add rows here.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <div className="legacy-section">
              <div className="legacy-section-head">
                <h3>Exclusions</h3>
                <button
                  className="module-btn legacy-add-btn"
                  type="button"
                  onClick={handleAddInstructionClick}
                >
                  Add
                </button>
              </div>
              <table className="module-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Order</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(instructions || []).map((inst) => (
                    <tr key={inst.id}>
                      <td>{inst.name}</td>
                      <td>{inst.order}</td>
                      <td className="module-actions">
                        <Link
                          to={`/treks/${selectedTrekId}/instructions/${inst.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="module-link-btn"
                          style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                          onClick={() => {
                            if (selectedTrekId) persistDraftForNestedNavigation(selectedTrekId);
                          }}
                        >
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="module-link-btn danger"
                          onClick={() => {
                            if (!window.confirm('Delete this exclusion?')) return;
                            handleDeleteInstruction(inst.id);
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!instructions.length ? (
                    <tr>
                      <td colSpan={3}>No exclusions added yet. These show on the event detail page.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </form>
      </div>
      ) : null}

    </div>
  );
};

export default Treks;
